// Typed frontend API client.

import { inviteContactHeaders, inviteContactHeadersForToken } from './invite-contact-key';
import { capabilityTokenInit, capabilityTokenPathSegment } from './capabilityToken';
import { compareReadsNewestFirst } from "./read-order";
import { API_BASE } from "./api-url";
import { clearHandoffCredentials } from "./auth/handoffCredential";
import { SESSION_TOKEN_KEY, clearLegacySessionStorage, sessionRequestHeaders } from "./session-headers";
import { syncWorkspaceIdentity, getStoredWorkspaceMode, type WorkspaceMode } from "./persona";
import { dealExternalRef } from "./deal-external-ref";
import { DIMENSION_META, coverageTierForPct, COMPOSITE_CONFIDENCE_FLOOR, type Dimension } from "./scoring";
import { severityBandForScore, dimensionToneForScore } from "./scoring/severity-band";
import type { CoverageBand } from "./scoring/read-band";
import { customerReadingName, customerSourceLabel } from "./evidence-presentation";
import type { Stage } from "@/lib/scoring/stages";
import type {
  DimensionScoreRow,
  FindingCard,
  FindingSeverity,
  ReportConfidenceInput,
  ReportConnectorWarning,
  ReportDimensionEvidence,
  ReportDimensionEvidenceOnlyRow,
  ReportData,
  SeverityBand,
} from "@/types/report";
import type { DealSummary, PortCoSummary } from "@/types/portfolio";

export interface ServiceStatus {
  id: string;
  label: string;
  provisioned: boolean;
  // Raw env var NAMES.
  missing?: string[];
  missing_count?: number;
  auth_config_present?: boolean;
  /** True when THIS workspace already holds a live connection for the source, on any rail. */
  has_connection?: boolean;
  why: string;
  docs_url?: string;
}

export type SourceSystemIntegrationStatus =
  | "live"
  | "proxy"
  | "manual-ingest"
  | "absent";
export type SourceSystemCoverageStatus =
  | "synced"
  | "authorized"
  | "available"
  | "manual"
  | "unbuilt";

export interface AdminSourceSystemRow {
  id: string;
  source_system: string;
  total_weight_pct: number;
  metric_count: number;
  auto_count: number;
  manual_count: number;
  cadence: string;
  example_tools: string[];
  sample_metrics_fed: string;
  integration_status: SourceSystemIntegrationStatus;
  source_ids: string[];
  status_detail: string;
  coverage_status: SourceSystemCoverageStatus;
  coverage_label: string;
  covered_weight_pct: number;
}

export interface AdminSourceSystemsResponse {
  total_systems: number;
  total_metric_count: number;
  total_weight_pct: number;
  covered_weight_pct: number;
  status_counts: Record<SourceSystemIntegrationStatus, number>;
  systems: AdminSourceSystemRow[];
}

export async function fetchAdminSourceSystems(): Promise<AdminSourceSystemsResponse> {
  return http<AdminSourceSystemsResponse>("/api/admin/source-systems");
}

export interface CredentialsStatusResponse {
  services: ServiceStatus[];
  provisioned_count: number;
  missing_count: number;
  total: number;
}

export interface GithubSyncProgress {
  selection_key: string;
  repositories: Array<{
    full_name: string;
    status: 'succeeded' | 'truncated' | 'failed' | 'pending';
    records_synced: number;
    records_changed: number;
    error?: string;
    truncated_streams?: string[];
  }>;
  timed_out: boolean;
}

export interface SourceListItem {
  id: string;
  label: string;
  kind: "oauth" | "web";
  credential_service: string | null;
  provisioned: boolean;
  /** The scopes Zoe asks the provider for. Always present, connected or not. */
  scopes: string[];
  /** The scopes the PROVIDER reported granting, when it reported any. */
  granted_scopes?: string[] | null;
  scopes_provenance?: "granted" | "requested";
  what_we_read: string[];
  what_we_dont: string[];
  metric_count: number;
  weight_pct?: number;
  connected?: boolean;
  connection_status?: "disconnected" | "connected" | "syncing" | "error";
  auth_provider?: "direct" | "composio";
  connect_mode?: "oauth_redirect" | "composio_connect";
  /** False when this source has no Composio auth config, so no connect attempt can succeed. */
  auth_config_present?: boolean;
  /** The live connection row for this source, when one exists. */
  connection_id?: string | null;
  selected_repositories?: GithubRepositorySelection[];
  github_sync?: GithubSyncProgress | null;
  github_sync_pending?: boolean;
  /** False when the company contact owns this connection's retry action. */
  github_sync_retry_allowed?: boolean;
  /** QuickBooks is connected but Zoe holds no company id, so every read is skipped. */
  quickbooks_realm_missing?: boolean;
  install_url?: string | null;
  readiness: {
    status: "ready" | "missing_credentials";
    action: "start_oauth" | "provision_oauth_app" | "start_composio" | "provision_composio" | "enter_company_url";
    detail: string;
  };
  data_freshness: {
    status: "not_synced" | "syncing" | "fresh" | "stale" | "error";
    label: string;
    last_success_at?: string | null;
  };
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${status}`);
  }
}

/** Human-facing, non-retryable explanation for the inward-read authorization gate. */
export function operatorInwardReadBlockerMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 403 || typeof error.body !== 'object' || error.body === null) {
    return null;
  }
  const code = (error.body as { error?: unknown }).error;
  if (code === 'operator_beta_disabled') {
    return 'Operator diagnostics are not enabled for this workspace yet. This cannot be retried until the beta is enabled.';
  }
  if (code === 'operator_approval_required') {
    return 'Operator diagnostics need workspace approval before they can run. This cannot be retried until approval is recorded.';
  }
  return null;
}

export { SESSION_TOKEN_KEY };

let sessionCredentialEpoch = 0;

/** Monotonic count of session-credential changes in this page's lifetime. */
export function sessionCredentialGeneration(): number {
  return sessionCredentialEpoch;
}

/** Record that this browser's session credential changed. */
function noteSessionCredentialChanged(): void {
  sessionCredentialEpoch += 1;
  // A parked hand-off belongs to the credential being replaced; never let a
  // later /signin in this tab pick a dead one back up.
  clearHandoffCredentials();
}

/** Record a successful cookie sign-in without retaining the response session ID. */
export function noteSessionEstablished(): void {
  noteSessionCredentialChanged();
  clearLegacySessionStorage();
}

/** Clear legacy storage and invalidate session-dependent caches. */
export function clearSessionToken(): void {
  noteSessionCredentialChanged();
  clearLegacySessionStorage();
}

export interface UpgradeRequiredBody {
  error: 'upgrade_required';
  code?: 'upgrade_required';
  feature?: string;
  /** The plan the workspace holds now; `cta.plan` is the plan the way out points at. */
  plan?: string;
  cta?: string | { label?: string; href?: string; plan?: string };
  usage?: number;
  limit?: number;
  detail?: string;
}

export function isUpgradeRequiredError(error: unknown): error is ApiError & { body: UpgradeRequiredBody } {
  return (
    error instanceof ApiError &&
    error.status === 402 &&
    typeof error.body === 'object' &&
    error.body !== null &&
    'error' in error.body &&
    (error.body as { error?: unknown }).error === 'upgrade_required'
  );
}

export function upgradeRequiredMessage(error: unknown, fallback = 'Upgrade to a paid plan to continue.'): string | null {
  if (!isUpgradeRequiredError(error)) return null;
  const body = error.body;
  if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;
  if (typeof body.cta === 'string') return body.cta;
  if (body.cta?.label) return body.cta.label;
  return fallback;
}

export interface PaywallRequiredBody {
  error: 'paywall_required';
  code?: 'paywall_required';
  reason?: 'investor_hard_paywall';
  plan?: string;
  cta?: { label?: string; href?: string; plan?: string };
}

/** True for the 402 the API returns when the investor hard paywall blocks entry. */
export function isPaywallRequiredError(error: unknown): error is ApiError & { body: PaywallRequiredBody } {
  return (
    error instanceof ApiError &&
    error.status === 402 &&
    typeof error.body === 'object' &&
    error.body !== null &&
    'error' in error.body &&
    (error.body as { error?: unknown }).error === 'paywall_required'
  );
}

/** Route of the enrollment interstitial. */
export const TWO_FACTOR_SETUP_PATH = "/security/two-factor";

/** True for the 403 the API returns when a session has no second factor. */
export function isTwoFactorEnrollmentRequiredError(
  error: unknown,
): error is ApiError & { body: { error: "two_factor_enrollment_required" } } {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    typeof error.body === "object" &&
    error.body !== null &&
    "error" in error.body &&
    (error.body as { error?: unknown }).error === "two_factor_enrollment_required"
  );
}

function redirectToTwoFactorSetup(): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname === TWO_FACTOR_SETUP_PATH) return;
  window.location.assign(TWO_FACTOR_SETUP_PATH);
}

/** Where an idle-timed-out session lands. */
export const INACTIVITY_SIGNIN_PATH = "/signin?reason=inactivity";

function redirectToInactivitySignIn(): void {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (path === "/signin" || path === "/login" || path === "/sign-up") return;
  const next = encodeURIComponent(path + window.location.search);
  window.location.assign(`${INACTIVITY_SIGNIN_PATH}&next=${next}`);
}

export interface SessionHeartbeat {
  idle_window_ms: number | null;
  remaining_ms: number | null;
  expires_at: string | null;
}

export async function fetchSessionHeartbeat(activity = true): Promise<SessionHeartbeat> {
  const path = activity ? "/api/auth/heartbeat" : "/api/auth/heartbeat?activity=false";
  return http<SessionHeartbeat>(path, { method: "POST" });
}

export type PlanId = 'free' | 'deal' | 'desk' | 'portfolio';

export function normalizePlanId(raw: string): PlanId {
  switch (raw) {
    case 'free':
    case 'deal':
    case 'desk':
    case 'portfolio':
      return raw;
    case 'investor_pro':
    case 'investor_pro_annual':
      return 'desk';
    case 'investor_enterprise':
      return 'portfolio';
    default:
      // Unknown strings fail closed: an unrecognized plan entitles nothing.
      return 'free';
  }
}

/** Why an upgrade gate fired: a paid plan whose payment lapsed, the one-company allowance, or the monthly Read allowance. */
export type GateReason = 'payment_lapsed' | 'company_limit' | 'monthly_limit';

export interface GateCta {
  href: string;
  /** Button label taken from the server payload. */
  label: string;
  /** Which 402 gate fired, so callers can pick the right Zoe-voiced explanation. */
  kind: 'upgrade' | 'paywall';
  /** The plan the server's way out points at. */
  plan?: PlanId;
  /** Absent when the payload does not say which allowance ran out. */
  reason?: GateReason;
  /** The allowance the server enforced, when it sent one. */
  limit?: number;
}

function upgradeGateReason(feature: unknown, heldPlan: PlanId | undefined, targetPlan: PlanId | undefined): GateReason | undefined {
  // A lapsed paid plan's way out points back at the plan it already holds (entitlements.ts recoveryCtaForLapsedPlan, pinned by payment-failure.test.ts).
  if (heldPlan && heldPlan !== 'free' && targetPlan === heldPlan) return 'payment_lapsed';
  if (feature === 'scored_case') return 'company_limit';
  if (feature === 'diagnostic') return 'monthly_limit';
  return undefined;
}

export function gateCtaFromError(error: unknown): GateCta | null {
  if (isPaywallRequiredError(error)) {
    const cta = error.body.cta;
    return {
      href: cta?.href ?? '/settings?tab=billing',
      label: cta?.label ?? 'Start your subscription',
      kind: 'paywall',
    };
  }
  if (isUpgradeRequiredError(error)) {
    const { cta, feature, limit } = error.body;
    const rawPlan = typeof cta === 'object' ? cta?.plan : undefined;
    const plan = rawPlan ? normalizePlanId(rawPlan) : undefined;
    const heldPlan = typeof error.body.plan === 'string' ? normalizePlanId(error.body.plan) : undefined;
    const reason = upgradeGateReason(feature, heldPlan, plan);
    return {
      href: typeof cta === 'object' && cta?.href ? cta.href : '/settings?tab=billing',
      label: typeof cta === 'string' ? cta : cta?.label ?? 'Upgrade',
      kind: 'upgrade',
      plan,
      ...(reason ? { reason } : {}),
      ...(typeof limit === 'number' && Number.isFinite(limit) && limit > 0 ? { limit } : {}),
    };
  }
  return null;
}

export class CredentialsMissingApiError extends ApiError {
  constructor(public readonly service: ServiceStatus) {
    super(503, { error: "credentials_missing", service });
    this.name = "CredentialsMissingApiError";
  }
}

export class ApiUnreachableError extends Error {
  constructor(
    public readonly base_url: string,
    public readonly cause: unknown,
  ) {
    super(`API at ${base_url} is unreachable. Backend not deployed yet.`);
    this.name = "ApiUnreachableError";
  }
}

export class ApiTimeoutError extends Error {
  constructor(
    public readonly path: string,
    public readonly timeout_ms: number,
  ) {
    super(`No answer from ${path} within ${Math.round(timeout_ms / 1000)}s.`);
    this.name = "ApiTimeoutError";
  }
}

export function authorizedFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...sessionRequestHeaders(),
      ...(init?.headers ?? {}),
    },
  });
}

async function http<T>(path: string, init?: RequestInit, options?: { timeoutMs?: number }): Promise<T> {
  let res: Response;
  const timeoutMs = options?.timeoutMs;
  const controller = timeoutMs !== undefined ? new AbortController() : null;
  const timer =
    controller !== null && timeoutMs !== undefined
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: "include",
      ...(controller ? { signal: controller.signal } : {}),
      headers: {
        "Content-Type": "application/json",
        ...sessionRequestHeaders(),
        ...inviteContactHeaders(path),
        ...(init?.headers ?? {}),
      },
    });
  } catch (e) {
    if (controller?.signal.aborted && timeoutMs !== undefined) {
      throw new ApiTimeoutError(path, timeoutMs);
    }
    // Network error (DNS, CORS, offline) vs HTTP error — distinguish them.
    throw new ApiUnreachableError(API_BASE, e);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 503 && body?.error === "credentials_missing") {
      throw new CredentialsMissingApiError(body.service);
    }
    if (res.status === 401 && body?.error === "unauthorized") {
      clearSessionToken();
      if (body?.reason === "idle_timeout") {
        redirectToInactivitySignIn();
      }
    }
    // The account has no second factor, so nothing but the enrollment escape list will answer.
    if (res.status === 403 && body?.error === "two_factor_enrollment_required") {
      redirectToTwoFactorSetup();
    }
    throw new ApiError(res.status, body);
  }
  return body as T;
}

/** Request init for a public capability-token call. */
function capabilityInit(token: string, init?: RequestInit): RequestInit {
  return capabilityTokenInit(token, init);
}

/** As `capabilityInit`, plus invite contact key. */
function inviteInit(token: string, init?: RequestInit): RequestInit {
  return capabilityTokenInit(token, {
    ...init,
    headers: {
      ...inviteContactHeadersForToken(token),
      ...((init?.headers as Record<string, string> | undefined) ?? {}),
    },
  });
}

export async function fetchCredentialsStatus(): Promise<CredentialsStatusResponse> {
  return http<CredentialsStatusResponse>("/api/credentials/status");
}

export interface OutsideInTeaser {
  company_name: string;
  company_url: string | null;
  teaser_score: number;
  score_band: string;
  public_only: boolean;
  capped: boolean;
  depth: "public_teaser";
  domain_resolution?: "provided" | "resolved" | "unresolved";
  sector?: string | null;
  headcount_band?: string | null;
  observed_at: string;
  sources: Array<{
    id: string;
    label: string;
    status: string;
    signal: string;
    evidence: string;
    observed_at: string;
    citation?: { label: string; url: string };
  }>;
  findings: Array<{
    title: string;
    signal: string;
    evidence: string;
    capped: true;
    freshness: string;
    citation?: { label: string; url: string };
  }>;
  limitations: string[];
  next_step: { label: string; href: string };
}

export async function runPublicOutsideInScan(input: {
  company_name: string;
  company_url?: string;
  persona?: "investor" | "operator";
}): Promise<OutsideInTeaser> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/public/outside-in-scan`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        company_name: input.company_name,
        company_url: input.company_url,
        persona: input.persona ?? "investor",
      }),
    });
  } catch (e) {
    throw new ApiUnreachableError(API_BASE, e);
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body);
  return body as OutsideInTeaser;
}

export async function fetchSources(dealId?: string | null): Promise<SourceListItem[]> {
  const query = dealId ? `?deal_id=${encodeURIComponent(dealId)}` : "";
  return http<SourceListItem[]>(`/api/oauth/sources${query}`);
}

export interface WorkspaceConnectionRow {
  id: string;
  source: string;
  deal: { id: string; codename: string } | null;
  filled_by: "investor_self" | "target_owner";
  owner_email: string | null;
  connected_at: string;
  last_synced_at: string | null;
  sync_status?: 'idle' | 'syncing' | 'failed' | 'synced' | 'not_synced' | 'partial';
  github_sync?: GithubSyncProgress | null;
  github_sync_pending?: boolean;
  auth_provider: string;
  granted_scopes?: string[] | null;
  scopes_provenance?: "granted" | "requested";
  nango_connection_id: string | null;
  shared: boolean;
  shared_with: Array<{ deal_id: string | null; deal_codename: string | null; source: string }>;
  exclusive_family?: string | null;
  is_sandbox_clone?: boolean;
  /** ZPM-366: the probe verdict, so a lapsed credential stops reading as connected. */
  health_status?: "healthy" | "needs_reconnect" | "failed" | "needs_selection" | null;
  health_reason?: string | null;
  health_checked_at?: string | null;
  reconnect_required?: boolean;
  /** ZMPP-22: the investor revoked the invite this connection came through; it no longer syncs or feeds a Read. */
  access_revoked?: boolean;
  access_revoked_at?: string | null;
  /** ZMPP-22 T7: whether the contact's consent behind this connection matches the current terms, and whether that pauses it. */
  consent_current?: boolean;
  consent_paused?: boolean;
}

export interface WorkspaceConnectionSourceGroup {
  source: string;
  label: string;
  connections: WorkspaceConnectionRow[];
}

export interface WorkspaceFamilyConflict {
  family: string;
  family_label: string;
  deal_id: string | null;
  deal_codename: string | null;
  sources: string[];
  members: Array<{
    connection_id: string;
    source: string;
    source_label: string;
    connected_at: string;
    last_synced_at?: string | null;
  }>;
  primary_source?: string;
  primary_source_label?: string;
  note: string;
}

export interface WorkspaceConnectionsResponse {
  sources: WorkspaceConnectionSourceGroup[];
  total: number;
  /** Optional: an older API build omits it, and the view shows nothing rather than inventing a conflict. */
  family_conflicts?: WorkspaceFamilyConflict[];
}

export async function fetchWorkspaceConnections(): Promise<WorkspaceConnectionsResponse> {
  return http<WorkspaceConnectionsResponse>("/api/connections");
}

export interface SourceOfTruthResult {
  family: string;
  family_label: string;
  source: string;
  source_label: string;
  deal_id: string | null;
  outcome: "superseded" | "armed" | "noop";
  superseded: Array<{ connection_id: string; source: string; source_label: string; connected_at: string }>;
  armed: Array<{ connection_id: string; source: string; source_label: string; connected_at: string }>;
  detail: string;
}

export async function declareSourceOfTruth(source: string, dealId: string | null): Promise<SourceOfTruthResult> {
  return http<SourceOfTruthResult>("/api/connections/source-of-truth", {
    method: "POST",
    body: JSON.stringify({ source, deal_id: dealId }),
  });
}

// Phase 1 of docs/connection-model-and-settings.md.4 — deal-scoped, refcounted disconnect.
export async function disconnectDealConnection(
  dealCodename: string,
  source: string,
): Promise<{
  deal_codename: string;
  source: string;
  status: "disconnected";
  revoked_local_connections: number;
  revoked_provider_connections: number;
}> {
  return http(`/api/deals/pipeline/${encodeURIComponent(dealCodename)}/connections/${encodeURIComponent(source)}/disconnect`, {
    method: "POST",
  });
}

export async function sandboxCloneDealConnection(
  dealCodename: string,
  source: string,
): Promise<{
  deal_codename: string;
  source: string;
  status: "cloned";
  connection_id: string;
  connector_records_cloned: number;
  /** Other sandbox connections stood down to make room under the one-CRM-per- case rule. */
  superseded_sandbox_sources?: string[];
}> {
  return http(`/api/deals/pipeline/${encodeURIComponent(dealCodename)}/connections/${encodeURIComponent(source)}/sandbox-clone`, {
    method: "POST",
  });
}

export async function removeSandboxCloneDealConnection(
  dealCodename: string,
  source: string,
): Promise<{
  deal_codename: string;
  source: string;
  status: "removed";
  connector_records_removed: number;
}> {
  return http(`/api/deals/pipeline/${encodeURIComponent(dealCodename)}/connections/${encodeURIComponent(source)}/sandbox-clone/remove`, {
    method: "POST",
  });
}

export interface SandboxRecordLever {
  pointer: Array<string | number>;
  pointer_path: string;
  label: string;
  value: unknown;
  value_type: "number" | "string" | "boolean" | "other";
  /** score-stage.ts RawMetric key(s) this field feeds; empty means visible but not confirmed to move any score. */
  feeds: string[];
  confidence: "known" | "heuristic" | "unclassified";
}

export interface SandboxRecordSummary {
  id: string;
  model: string;
  external_id: string;
  last_seen_at: string;
  changed_at: string;
  lever_count: number;
  scored_lever_count: number;
}

export async function listSandboxRecords(
  dealCodename: string,
  source: string,
): Promise<{ deal_codename: string; source: string; connection_id: string; records: SandboxRecordSummary[] }> {
  return http(`/api/deals/pipeline/${encodeURIComponent(dealCodename)}/connections/${encodeURIComponent(source)}/sandbox-records`);
}

export async function getSandboxRecord(
  dealCodename: string,
  source: string,
  recordId: string,
): Promise<{
  deal_codename: string;
  source: string;
  record: { id: string; model: string; external_id: string; raw_json: Record<string, unknown>; levers: SandboxRecordLever[] };
}> {
  return http(
    `/api/deals/pipeline/${encodeURIComponent(dealCodename)}/connections/${encodeURIComponent(source)}/sandbox-records/${encodeURIComponent(recordId)}`,
  );
}

export async function editSandboxRecord(
  dealCodename: string,
  source: string,
  recordId: string,
  pointer: Array<string | number>,
  value: unknown,
): Promise<{
  deal_codename: string;
  source: string;
  status: "edited";
  changed: boolean;
  record: { id: string; model: string; external_id: string; raw_json: Record<string, unknown>; levers: SandboxRecordLever[] };
}> {
  return http(
    `/api/deals/pipeline/${encodeURIComponent(dealCodename)}/connections/${encodeURIComponent(source)}/sandbox-records/${encodeURIComponent(recordId)}/edit`,
    { method: "POST", body: JSON.stringify({ pointer, value }) },
  );
}

export type SandboxConfidenceBand =
  | "low"
  | "under_floor"
  | "over_floor"
  | "medium"
  | "high_medium"
  | "high";

/** What a read confidence figure earns at the shipped ladder. */
export type SandboxPresetGateDecision = "low_confidence" | "self_attest" | "auto_sign";
export type SandboxPresetCrmSource = "hubspot" | "salesforce";

export interface SandboxPresetLever {
  id: string;
  label: string;
  value: number;
  unit: "usd" | "count" | "pct" | "months" | "score";
  /** score-stage.ts RawMetric key(s) this number reaches. */
  feeds: string[];
  source: string;
  describe: string;
}

export interface SandboxConfidencePreset {
  band: SandboxConfidenceBand;
  /** The label. The pinned number IS the name of the preset. */
  title: string;
  /** The pinned read-confidence percentage, 0-100. */
  reads_pct: number;
  /** What that number earns at the gate, in a few words. */
  gate_note: string;
  /** The same number, recomputed server-side off the real scorer. */
  predicted_reads_pct: number;
  gate_decision: SandboxPresetGateDecision;
  /** The pinned number as a bare percentage string, for prose. */
  target: string;
  summary: string;
  sources: string[];
  seeds_data_room_csv: boolean;
  levers: SandboxPresetLever[];
}

export async function fetchSandboxConfidencePresets(
  dealCodename: string,
  crm: SandboxPresetCrmSource = "hubspot",
): Promise<{
  deal_codename: string;
  crm: SandboxPresetCrmSource;
  stage: string;
  thresholds: { self_attest_pct: number; auto_sign_pct: number };
  bands: Record<SandboxConfidenceBand, string>;
  presets: SandboxConfidencePreset[];
}> {
  return http(
    `/api/deals/pipeline/${encodeURIComponent(dealCodename)}/sandbox-presets?crm=${encodeURIComponent(crm)}`,
  );
}

export interface SandboxPresetPrediction {
  deal_codename: string;
  band: SandboxConfidenceBand;
  crm: SandboxPresetCrmSource;
  /** What the values currently in the dialog would read. */
  reads_pct: number;
  decision: SandboxPresetGateDecision;
  self_attest_pct: number;
  auto_sign_pct: number;
  stage: string;
  pinned_reads_pct: number;
  /** False when an edit changed WHICH readings exist, not just what they say. */
  matches_pinned: boolean;
}

/** What the edited values would read, computed by the API off the real scorer and written nowhere. */
export async function predictSandboxConfidencePreset(
  dealCodename: string,
  band: SandboxConfidenceBand,
  input: { crm: SandboxPresetCrmSource; overrides: Record<string, number> },
): Promise<SandboxPresetPrediction> {
  return http(
    `/api/deals/pipeline/${encodeURIComponent(dealCodename)}/sandbox-presets/${encodeURIComponent(band)}/predict`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export interface SandboxPresetSeedResult {
  deal_codename: string;
  band: SandboxConfidenceBand;
  crm: SandboxPresetCrmSource;
  status: "seeded" | "partially_seeded";
  target: string;
  /** What the case now reads, for the values that were actually written. */
  reads_pct?: number;
  /** What the preset's label promises, unedited. */
  pinned_reads_pct?: number;
  gate_decision?: SandboxPresetGateDecision;
  /** False when the seed was partial, or an edit removed readings. */
  matches_pinned?: boolean;
  /** The seed landed durably, but the derived relational `connections` mirror did not. */
  canonical_mirror_degraded?: boolean;
  canonical_mirror_detail?: string | null;
  sources_seeded: Array<{ source: string; connection_id: string; connector_records_seeded: number }>;
  sources_refused: Array<{ source: string; error: string; detail?: string }>;
  data_room_document_id: string | null;
  connector_records_seeded: number;
  /** What the seed cleared off the case first, so the band's evidence set is exactly the band's. */
  evidence_reset?: {
    connections_removed: string[];
    connector_records_removed: number;
    data_room_documents_removed: number;
    web_scan_records_removed: number;
  };
}

export const SANDBOX_SEED_TIMEOUT_MS = 90_000;

export async function seedSandboxConfidencePreset(
  dealCodename: string,
  band: SandboxConfidenceBand,
  input: { crm: SandboxPresetCrmSource; overrides: Record<string, number> },
): Promise<SandboxPresetSeedResult> {
  return http(
    `/api/deals/pipeline/${encodeURIComponent(dealCodename)}/sandbox-presets/${encodeURIComponent(band)}/seed`,
    { method: "POST", body: JSON.stringify(input) },
    { timeoutMs: SANDBOX_SEED_TIMEOUT_MS },
  );
}

export type RagQuestionKind = "gap_challenge" | "verification_request";
export type RagDiscrepancyStatus = "matched" | "gap" | "unverifiable";
export type RagDiscrepancySeverity = "none" | "low" | "medium" | "high";

export interface RagClaimQuestion {
  id: string;
  kind: RagQuestionKind;
  priority: "high" | "medium" | "low";
  source_ref?: string;
  question: string;
  tension: {
    status: RagDiscrepancyStatus;
    severity: RagDiscrepancySeverity;
    rationale: string;
    delta: { absolute: number | null; percent: number | null };
    binding: {
      metric_key: string;
      portco_slug: string | null;
      status: "bound" | "unverifiable";
      reason: string;
      claimed_value: { raw: string | null; value: number | null; unit: string };
      system_value: {
        raw: string | null;
        value: number | null;
        unit: string;
        source_table: string;
        source_field: string;
        observed_at: string;
      } | null;
      claim: {
        id: string;
        claim: string;
        topic: string;
        materiality: "high" | "medium";
        source: {
          title: string | null;
          source: string;
          trust: string;
          observed_at: string | null;
          citation_ref: string;
        };
        evidence_excerpt: string;
      };
    };
  };
}

export async function fetchRagQuestions(opts: { limit?: number; source?: string; external_ref?: string } = {}): Promise<RagClaimQuestion[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.source) params.set("source", opts.source);
  if (opts.external_ref) params.set("external_ref", opts.external_ref);
  const body = await http<{ questions?: RagClaimQuestion[] }>(
    `/api/rag/questions${params.size ? `?${params.toString()}` : ""}`,
  );
  return Array.isArray(body.questions) ? body.questions : [];
}

export interface RagMaterialClaim {
  id: string;
  claim: string;
  topic: string;
  materiality: "high" | "medium";
  source: {
    title: string | null;
    source: string;
    trust: string;
    observed_at: string | null;
    external_ref: string | null;
    citation_ref: string;
  };
  evidence_excerpt: string;
}

export async function fetchRagClaims(opts: { limit?: number; source?: string; external_ref?: string } = {}): Promise<RagMaterialClaim[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.source) params.set("source", opts.source);
  if (opts.external_ref) params.set("external_ref", opts.external_ref);
  const body = await http<{ claims?: RagMaterialClaim[] }>(
    `/api/rag/claims${params.size ? `?${params.toString()}` : ""}`,
  );
  return Array.isArray(body.claims) ? body.claims : [];
}

export interface GithubRepositorySelection {
  id?: string;
  name?: string;
  full_name: string;
  private?: boolean;
  selected_at?: string;
}

export interface GithubAccessibleRepository {
  id: string;
  name: string;
  full_name: string;
  private: boolean;
  owner_login: string | null;
  owner_type: string | null;
  pushed_at: string | null;
  selected: boolean;
}

export interface GithubRepositoriesResponse {
  selected_repositories: GithubRepositorySelection[];
  repositories: GithubAccessibleRepository[];
  /** The company contact owns this GitHub grant; only they can change its repositories. */
  contact_owned?: boolean;
  install_url?: string | null;
}

export async function fetchGithubRepositories(): Promise<GithubRepositoriesResponse> {
  return http<GithubRepositoriesResponse>("/api/connections/github/repositories");
}

export async function updateGithubRepositorySelection(
  repositories: Array<
    Pick<GithubRepositorySelection, "full_name" | "id" | "name" | "private">
  >,
): Promise<{
  status: "ok";
  selected_repositories: GithubRepositorySelection[];
}> {
  return http<{
    status: "ok";
    selected_repositories: GithubRepositorySelection[];
  }>("/api/connections/github/repositories", {
    method: "POST",
    body: JSON.stringify({ repositories }),
  });
}

export type GithubRepositorySelectionRow = Pick<
  GithubRepositorySelection,
  "full_name" | "id" | "name" | "private"
>;

export interface GithubRepositorySelectionSaveResponse {
  status: "ok";
  selected_repositories: GithubRepositorySelection[];
  sync_queued?: boolean;
}

/** Token-scoped GitHub repository picker for the delegated owner / investor self-connect flow. */
export async function fetchInviteGithubRepositories(
  token: string,
): Promise<GithubRepositoriesResponse> {
  return http<GithubRepositoriesResponse>(
    `/api/invites/${capabilityTokenPathSegment(token)}/github/repositories`,
    inviteInit(token),
  );
}

export async function updateInviteGithubRepositorySelection(
  token: string,
  repositories: GithubRepositorySelectionRow[],
): Promise<GithubRepositorySelectionSaveResponse> {
  return http<GithubRepositorySelectionSaveResponse>(
    `/api/invites/${capabilityTokenPathSegment(token)}/github/repositories`,
    inviteInit(token, { method: "POST", body: JSON.stringify({ repositories }) }),
  );
}

/** Deal-scoped authed GitHub repository picker. */
export async function fetchDealGithubRepositories(
  codename: string,
): Promise<GithubRepositoriesResponse> {
  return http<GithubRepositoriesResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/github/repositories`,
  );
}

export async function updateDealGithubRepositorySelection(
  codename: string,
  repositories: GithubRepositorySelectionRow[],
): Promise<GithubRepositorySelectionSaveResponse> {
  return http<GithubRepositorySelectionSaveResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/github/repositories`,
    { method: "POST", body: JSON.stringify({ repositories }) },
  );
}

/** Resume the exact case connection without changing its saved selection. */
export async function retryDealGithubSync(codename: string): Promise<void> {
  await http(`/api/deals/pipeline/${encodeURIComponent(codename)}/github/sync`, { method: 'POST' });
}

interface ApiPortfolioRow {
  portco_slug: string;
  display_name: string | null;
  sector: string | null;
  tags: string[];
  pinned: boolean;
  flight_risk_score: number;
  flight_risk_level: "low" | "medium" | "high";
  last_observed_at: string | null;
  open_insight_count: number;
  worst_severity: "ok" | "attn" | "crit" | null;
  flight_risk_has_data?: boolean;
  diagnostic_score?: number;
  diagnostic_severity?: SeverityBand;
  diagnostic_observed_at?: string;
  score_source?: "flight_risk" | "diagnostic_fallback" | "none";
  // Canonical current score + 30-day delta (single source of truth).
  overall_score?: number | null;
  overall_delta_30d?: number;
  overall_severity?: SeverityBand | null;
  overall_as_of?: string | null;
}

interface ApiDealPipelineEntry {
  request_lifecycle_statuses?: DealSummary['request_lifecycle_statuses'];
  id: string;
  deal_codename: string;
  status:
    | "lead"
    | "sourced"
    | "qualified"
    | "diligence"
    | "ic_review"
    | "term_sheet"
    | "closed_won"
    | "closed_lost"
    | "on_hold";
  size_usd?: number;
  sector?: string;
  note?: string;
  next_step?: string;
  command_note?: string;
  request_state?: string;
  request_label?: string;
  display_rank?: number;
  score_preliminary?: boolean;
  created_at: string;
  updated_at: string;
  updated_by_user_id: string;
  invite_status?: "not_sent" | "link_created" | "email_sent" | "email_failed";
  source_status?: Record<
    string,
    "not_connected" | "connected" | "error" | "declined"
  >;
  requested_sources?: string[];
  zoe_score?: number;
  severity?: SeverityBand;
  diagnostic_id?: string;
  score_observed_at?: string;
  company_url?: string;
  case_lifecycle?: 'archived' | 'watching';
  archived_at?: string;
  watching_since?: string;
  /** G1 — set when the deal's latest delivered read is labelled low confidence. */
  low_confidence?: boolean;
  low_confidence_reason?: string;
  low_confidence_diagnostic_id?: string;
  /** Whether ANY diagnostic — complete, in flight, or failed — exists for this codename. */
  has_diagnostic?: boolean;
}

// ── Sync run ledger (GET /api/sync-runs) ────────────────────────────────────
export interface SyncRunItem {
  id: string;
  source: string;
  status: "running" | "succeeded" | "failed";
  started_at: string;
  finished_at: string | null;
  records_synced: number | null;
}

export async function fetchSyncRuns(limit = 50): Promise<SyncRunItem[]> {
  const body = await http<{ total: number; runs: SyncRunItem[] }>(
    `/api/sync-runs?limit=${limit}`,
  );
  return body.runs;
}

export interface PortcoPulseReadEvent {
  at: string;
  severity: "ok" | "attn" | "crit";
  title: string;
  note: string;
}

export interface PortcoPulseRead {
  portco_slug: string;
  as_of: string;
  window_start: string;
  window_end: string;
  score_from: number;
  score_to: number;
  /** Movement since the previous completed read (not the 30-day window). */
  delta_since_last_look: number;
  /** Canonical 30-day-window delta (score_to − score_from). */
  delta_30d?: number;
  open_thread?: string;
  suggested_question?: string;
  since_last_look: Array<{ marker: "plus" | "delta"; label: string; text: string }>;
  watch_triggers: Array<{ label: string; status: string; active: boolean }>;
  events: PortcoPulseReadEvent[];
  metrics_moved: Array<{ label: string; status: string; tone: "ok" | "bad" | "open" }>;
  next_reread: { label: string; at: string };
  sources_health: Array<{ label: string; synced_at: string }>;
}

/** Portco-scoped pulse read; resolves null when the company has none yet. */
export async function fetchPortcoPulseRead(
  slug: string,
): Promise<PortcoPulseRead | null> {
  try {
    return await http<PortcoPulseRead>(
      `/api/portcos/${encodeURIComponent(slug)}/pulse-read`,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export interface QuickBooksRevenueTrendPoint {
  month: string;
  observed_at: string;
  revenue_usd: number;
  mom_pct: number | null;
  yoy_pct: number | null;
}

export type RevenueTrendDirection = 'up' | 'down' | 'flat';

export interface QuickBooksRevenueTrend {
  slope_per_day: number;
  intercept: number;
  r_squared: number;
  direction: RevenueTrendDirection;
  delta_abs: number;
  delta_pct: number | null;
  n: number;
  start_at: string;
  end_at: string;
}

export interface QuickBooksRevenueTrendResponse {
  workspace_id: string;
  portco_slug: string;
  months: number;
  points: QuickBooksRevenueTrendPoint[];
  total_revenue_usd: number;
  ttm_revenue_usd: number;
  trend: QuickBooksRevenueTrend | null;
}

export async function fetchQuickBooksRevenueTrend(
  portcoSlug: string,
): Promise<QuickBooksRevenueTrendResponse> {
  return http<QuickBooksRevenueTrendResponse>(
    `/api/sources/quickbooks/revenue-trend?portco_slug=${encodeURIComponent(portcoSlug)}&months=12`,
  );
}

export async function fetchPortfolioSummaries(): Promise<PortCoSummary[]> {
  const body = await http<{ total: number; portcos: ApiPortfolioRow[] }>(
    "/api/portfolio",
  );
  return body.portcos.map((row) => {
    // Single source of truth: the canonical overall_score + overall_delta_30d resolved server-side.
    const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
    const hasCanonical = typeof row.overall_score === "number";
    const hasLiveScore = row.flight_risk_has_data !== false;
    const fallbackScore = typeof row.diagnostic_score === "number" ? clamp(row.diagnostic_score) : null;
    const hasScore = hasCanonical || hasLiveScore || fallbackScore !== null;
    const score = hasCanonical
      ? clamp(row.overall_score as number)
      : hasLiveScore
        ? clamp(100 - row.flight_risk_score)
        : fallbackScore ?? 0;
    const delta = hasCanonical && typeof row.overall_delta_30d === "number" ? Math.round(row.overall_delta_30d) : 0;
    const severity = hasCanonical
      ? row.overall_severity ?? severityBandForScore(score)
      : hasLiveScore
        ? severityBandForScore(score)
        : row.diagnostic_severity ?? (hasScore ? severityBandForScore(score) : "needs_attention");
    return {
      id: row.portco_slug,
      slug: row.portco_slug,
      name: row.display_name ?? humanizeApiSlug(row.portco_slug),
      sector: row.sector ?? "",
      stage: row.tags[0] ? humanizeApiSlug(row.tags[0]) : "",
      zoe_score: score,
      score_status: hasScore ? "known" : "insufficient",
      severity,
      delta_30d: delta,
      delta_90d: 0,
      velocity_30d: 0,
      top_dimension_flag: row.worst_severity
        ? {
            label: `${row.open_insight_count} open signals`,
            tone: row.worst_severity,
          }
        : undefined,
      sparkline_90d: [score],
      last_pulse_at: row.last_observed_at ?? new Date(0).toISOString(),
    };
  });
}

function mapApiDealPipelineEntry(row: ApiDealPipelineEntry): DealSummary {
  return {
    id: row.id,
    codename: row.deal_codename,
    stage: mapDealStatusToRouteStage(row.status, {
      hasCompletedReport: dealEntryHasCompletedReport(row),
      lowConfidence: row.low_confidence,
      hasAnyDiagnostic: row.has_diagnostic,
    }),
    pipeline_status: row.status,
    sector: row.sector ?? "—",
    inferred_sector: row.sector ?? null,
    committed_at: row.created_at,
    assigned_to: row.updated_by_user_id,
    invite_status: row.invite_status ?? "not_sent",
    request_lifecycle_statuses: row.request_lifecycle_statuses,
    source_status: row.source_status ?? {},
    requested_sources: row.requested_sources ?? [],
    zoe_score: typeof row.zoe_score === "number" ? row.zoe_score : undefined,
    severity: row.severity,
    note: row.note,
    next_step: row.next_step,
    command_note: row.command_note,
    request_state: row.request_state,
    request_label: row.request_label,
    display_rank: row.display_rank,
    score_preliminary: row.score_preliminary,
    company_url: row.company_url,
    case_lifecycle: row.case_lifecycle,
    archived_at: row.archived_at,
    watching_since: row.watching_since,
    low_confidence: row.low_confidence,
    low_confidence_reason: row.low_confidence_reason,
    low_confidence_diagnostic_id: row.low_confidence_diagnostic_id,
  };
}

export async function fetchDealPipelineSummaries(): Promise<DealSummary[]> {
  const body = await http<{ total: number; entries: ApiDealPipelineEntry[] }>(
    "/api/deals/pipeline",
  );
  return body.entries.map(mapApiDealPipelineEntry);
}

/**
 * Seal a codename into the pipeline. With `create`, the write opens a NEW company: a codename already
 * in use in the workspace is refused with 409 `codename_taken` (HRD-1-S08 T1) instead of overwriting it.
 */
export async function persistDealPipelineCodename(
  codename: string,
  company_url?: string,
  funding_stage?: Stage | null,
  sector?: string | null,
  real_target_name?: string,
  opts?: { create?: boolean },
): Promise<DealSummary> {
  const trimmedSector = sector?.trim();
  const trimmedRealName = real_target_name?.trim();
  const row = await http<ApiDealPipelineEntry>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}`,
    {
      method: "PUT",
      body: JSON.stringify({
        status: "lead",
        ...(company_url ? { company_url } : {}),
        ...(funding_stage !== undefined ? { funding_stage } : {}),
        ...(trimmedSector ? { sector: trimmedSector } : {}),
        ...(trimmedRealName ? { real_target_name: trimmedRealName } : {}),
        ...(opts?.create ? { create: true } : {}),
      }),
    },
  );
  return mapApiDealPipelineEntry(row);
}

/** Rename a case's codename. */
export async function renameDealPipelineCodename(from: string, to: string): Promise<DealSummary> {
  const row = await http<ApiDealPipelineEntry>(
    `/api/deals/pipeline/${encodeURIComponent(from)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ codename: to }),
    },
  );
  return mapApiDealPipelineEntry(row);
}

export async function setDealCaseLifecycle(
  codename: string,
  lifecycle: 'archived' | 'watching',
): Promise<DealSummary> {
  const row = await http<ApiDealPipelineEntry>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/lifecycle`,
    {
      method: 'PUT',
      body: JSON.stringify({ lifecycle }),
    },
  );
  return mapApiDealPipelineEntry(row);
}

export interface CloseCaseResult {
  id: string;
  codename: string;
  close_decision: 'invested' | 'passed';
  close_note?: string;
  closed_at: string;
}

export async function closeDiagnosticCase(
  ref: string,
  decision: 'invested' | 'passed',
  note?: string,
): Promise<CloseCaseResult> {
  return http<CloseCaseResult>(`/api/diagnostics/${encodeURIComponent(ref)}/close`, {
    method: 'POST',
    body: JSON.stringify(note ? { decision, note } : { decision }),
  });
}

/** Delete a Draft or Active case. */
export async function deleteDealPipelineEntry(codename: string): Promise<void> {
  await http<null>(`/api/deals/pipeline/${encodeURIComponent(codename)}`, {
    method: 'DELETE',
  });
}

export const DEAL_STAGE_LABELS: Record<ApiDealPipelineEntry["status"], string> = {
  lead: "Sourcing",
  sourced: "Sourcing",
  qualified: "Sourcing",
  diligence: "Diligence",
  ic_review: "Diligence",
  term_sheet: "Closing",
  closed_won: "Closed",
  closed_lost: "Passed",
  on_hold: "On hold",
};

export const DEAL_PIPELINE_BUCKETS: Record<ApiDealPipelineEntry["status"], string> = {
  ...DEAL_STAGE_LABELS,
  lead: "Outside-in",
};

export function dealStageLabel(status: DealSummary["pipeline_status"] | undefined): string {
  return status ? DEAL_STAGE_LABELS[status] : "Sourcing";
}

// True when the pipeline read model carries a signed, completed diagnostic for this case.
export function dealEntryHasCompletedReport(
  row: Pick<ApiDealPipelineEntry, "zoe_score" | "score_observed_at" | "diagnostic_id">,
): boolean {
  return (
    typeof row.zoe_score === "number" ||
    typeof row.score_observed_at === "string" ||
    typeof row.diagnostic_id === "string"
  );
}

export function mapDealStatusToRouteStage(
  status: ApiDealPipelineEntry["status"],
  opts?: { hasCompletedReport?: boolean; lowConfidence?: boolean; hasAnyDiagnostic?: boolean },
): DealSummary["stage"] {
  if (opts?.hasCompletedReport) return "report";
  // G1 — low confidence is a hint on a delivered read, never a route stop.
  if (
    status === "diligence" ||
    status === "ic_review" ||
    status === "term_sheet"
  ) {
    if (opts?.hasAnyDiagnostic === false) return "setup";
    return "running";
  }
  if (status === "closed_won" || status === "closed_lost") return "report";
  return "setup";
}

function humanizeApiSlug(slug: string): string {
  return slug
    .split(/[-_]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function initOauth(source: string): void {
  // Top-level navigation so the OAuth round-trip works (cookies/redirects).
  if (typeof window !== "undefined") {
    window.location.href = `${API_BASE}/api/oauth/${source}/init`;
  }
}

export interface ComposioConnectionLinkResponse {
  configured: true;
  source: string;
  toolkit: string;
  connection_request_id: string;
  redirect_url: string;
  pending_deal_id: string | null;
}

export interface ComposioConnectionStatusResponse {
  configured: boolean;
  status?: "pending" | "active" | "failed" | "expired" | "unknown";
  connection_request_id?: string;
  connection_id?: string;
  connected_account_id?: string | null;
}

/** Starts a Composio authorization. */
export async function createComposioConnectionLink(
  source: string,
  scope?: {
    pending_deal_id?: string;
    subdomain?: "login" | "test";
    /** QuickBooks' company id (Intuit's realm). */
    realm_id?: string;
  },
): Promise<ComposioConnectionLinkResponse> {
  return http<ComposioConnectionLinkResponse>("/api/connections/composio/link", {
    method: "POST",
    body: JSON.stringify({ source, ...(scope ?? {}) }),
  });
}

/** Save the QuickBooks company id on a connection that already exists. */
export async function saveQuickBooksRealmId(
  connectionId: string,
  realmId: string,
): Promise<{ connection_id: string; quickbooks_realm_present: boolean }> {
  return http<{ connection_id: string; quickbooks_realm_present: boolean }>(
    `/api/connections/${encodeURIComponent(connectionId)}/quickbooks-realm`,
    { method: "POST", body: JSON.stringify({ realm_id: realmId }) },
  );
}

export async function fetchComposioConnectionStatus(
  connectionRequestId: string,
): Promise<ComposioConnectionStatusResponse> {
  return http<ComposioConnectionStatusResponse>(
    `/api/connections/composio/status?connection_request_id=${encodeURIComponent(connectionRequestId)}`,
  );
}

export async function disconnectSource(
  source: string,
  connectionId?: string,
): Promise<{
  status: "disconnected";
  source: string;
  revoked_local_connections: number;
  revoked_provider_connections: number;
}> {
  const query = connectionId ? `?connection_id=${encodeURIComponent(connectionId)}` : "";
  return http(`/api/oauth/${source}/disconnect${query}`, {
    method: "POST",
  });
}

export interface SyncErrorItem {
  id: string;
  workspace_id: string;
  source: string;
  kind:
    | "auth_expired"
    | "permission_denied"
    | "rate_limit"
    | "partial"
    | "unknown";
  message: string;
  occurred_at: string;
  endpoint?: string;
  category: "user_fixable" | "system";
  action: "reconnect" | "connect_source" | "select_repositories" | "add_quickbooks_realm" | "retrying" | "contact_support";
  action_label: string;
  action_url: string | null;
  plain_description: string;
}

export interface SyncErrorsResponse {
  total: number;
  by_source: Record<string, number>;
  errors: SyncErrorItem[];
}

export async function fetchSyncErrors(): Promise<SyncErrorsResponse> {
  return http<SyncErrorsResponse>("/api/sources/errors");
}

/** A figure held back while the rest of its connector reports. */
export interface WithheldMeasurement {
  /** The metric key, so the screen can name the figure in its own words. */
  key: string;
  /** What that figure counts, plural, e.g. "resolved issues with a due date". */
  needs: string;
  observed: number;
  minimum: number;
}

/** The minimum activity a connector needs before it can measure anything. */
export interface SyncMeasurementFloor {
  /** What the connector counts, plural, e.g. "issues resolved". */
  noun: string;
  minimum: number;
  window_days: number;
  observed: number;
  withheld: WithheldMeasurement[];
}

export interface SyncFreshnessSource {
  source: string;
  last_attempt_at: string | null;
  last_success_at: string | null;
  consecutive_failures: number;
  in_backoff: boolean;
  next_due_at: string | null;
  last_run_status: "running" | "succeeded" | "failed" | null;
  last_failure_reason?: string | null;
  last_finished_at?: string | null;
  records_synced: number | null;
  records_changed: number | null;
  auth_provider: "direct" | "composio" | null;
  scoring_status?:
    | "not_connected"
    | "connected_no_data"
    | "connected_no_scoring_data"
    | "connected_below_measurement_floor"
    | "connected_partial_measurement"
    | "scoring_ready";
  scoring_record_count?: number;
  raw_record_count?: number;
  /** Where this source stands against its connector's minimum-volume floor. */
  measurement_floor?: SyncMeasurementFloor | null;
  /** Post-connect health-probe verdict for the resolved connection. */
  health_status?: "healthy" | "needs_reconnect" | "failed" | "needs_selection" | null;
  health_reason?: string | null;
}

export interface SyncFreshnessResponse {
  latest_success_at: string | null;
  label: string;
  total: number;
  sources: SyncFreshnessSource[];
}

/** HRD-3-S01 T6: a manual sync is queued per connection; the response never waits for the provider. */
export interface ManualSyncResponse {
  total: number;
  jobs: Array<{
    job_id: string | null;
    source: string;
    connection_id: string | null;
    status: "enqueued" | "existing" | "overflow";
  }>;
  skipped: Array<{ source: string; reason: "not_connected" }>;
  freshness: SyncFreshnessResponse;
}

export async function fetchSyncFreshness(): Promise<SyncFreshnessResponse> {
  return http<SyncFreshnessResponse>("/api/sources/freshness");
}

export async function triggerManualSync(
  source?: string,
): Promise<ManualSyncResponse> {
  return http<ManualSyncResponse>("/api/sync-runs/manual", {
    method: "POST",
    body: JSON.stringify({ source, force: true }),
  });
}

export interface MeWorkspace {
  id: string;
  name: string;
  mode: "investor" | "operator";
  stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string | null;
  headcount_band?: string | null;
  company_url?: string | null;
  plan: PlanId;
  onboarding_completed_at?: string | null;
  /** Set only by approved Operator Beta redemption. */
  operator_access_approved_at?: string | null;
  /** Dev-only pinned clock (local ISO); present only for /api/dev sessions. */
  dev_now?: string | null;
}

export interface MeEntitlement {
  entry_gate: "enforced" | "auth_first";
  /** The workspace is being HELD OUT of the product right now. */
  paywall_required: boolean;
  entitled: boolean;
}

export interface MeBilling {
  /** 'past_due' warns; 'dunned_off' means write quotas dropped to Free. */
  state: "active" | "past_due" | "dunned_off";
  /** In-app route that fixes the card. */
  recovery_href: string;
}

export interface MeSecurity {
  two_factor_enrollment_required: boolean;
}

export interface MeResponse {
  user_id: string;
  workspace_id: string;
  role: "owner" | "admin" | "member" | "analyst" | "partner_reviewer";
  is_zoe_admin?: boolean;
  email: string | null;
  /** Personal display name the user set in Settings → Profile, or null. */
  display_name?: string | null;
  entitlement?: MeEntitlement;
  billing?: MeBilling;
  security?: MeSecurity;
  workspace?: MeWorkspace;
}

export async function fetchMe(): Promise<MeResponse> {
  const me = await http<MeResponse>("/api/me");
  // B-E2E-10: refresh the persisted workspace-label cache to the authoritative session identity on every /api/me.
  syncWorkspaceIdentity(me.workspace_id, me.workspace?.name);
  return me;
}

export interface UpdateWorkspaceProfileInput {
  name?: string;
  mode?: "investor" | "operator";
  stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string | null;
  headcount_band?: string | null;
  company_url?: string | null;
}

export async function updateWorkspaceProfile(input: UpdateWorkspaceProfileInput): Promise<MeWorkspace> {
  return http<MeWorkspace>("/api/workspaces/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

/** Set (or clear, with an empty string) the caller's personal display name. */
export async function updateProfileName(displayName: string): Promise<{ display_name: string | null }> {
  return http<{ display_name: string | null }>("/api/me/profile", {
    method: "PATCH",
    body: JSON.stringify({ display_name: displayName }),
  });
}

export interface WorkspaceSetting {
  id: string;
  workspace_id: string;
  key: string;
  value: string;
  updated_at: string;
  updated_by_user_id: string;
}

/** Read one workspace-level setting. Resolves to null when unset (the route 404s). */
export async function fetchWorkspaceSetting(key: string): Promise<WorkspaceSetting | null> {
  try {
    return await http<WorkspaceSetting>(`/api/workspace-settings/${encodeURIComponent(key)}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

/** Owner/admin/zoe_admin only — the route 403s for other roles. */
export async function setWorkspaceSetting(key: string, value: string): Promise<WorkspaceSetting> {
  return http<WorkspaceSetting>(`/api/workspace-settings/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify({ value }),
  });
}

export type NotificationKind =
  | "dunning"
  | "winback"
  | "at_risk_nudge"
  | "card_expiring"
  | "predeletion_warning"
  | "daily_digest"
  | "stale_sync"
  | "insight"
  | "diagnosis_ready";

export type NotificationChannel = "email" | "in_app" | "sms";

export interface NotificationPreference {
  kind: NotificationKind;
  channel: NotificationChannel;
  enabled: boolean;
  source: "default" | "user";
}

export async function fetchNotificationPrefs(): Promise<{ preferences: NotificationPreference[] }> {
  return http<{ preferences: NotificationPreference[] }>("/api/notification-prefs");
}

export async function updateNotificationPref(input: {
  kind: NotificationKind;
  channel: NotificationChannel;
  enabled: boolean;
}): Promise<Omit<NotificationPreference, "source"> & { id: string; updated_at: string }> {
  return http<Omit<NotificationPreference, "source"> & { id: string; updated_at: string }>("/api/notification-prefs", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export interface ApiKeySummary {
  id: string;
  label: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  /** True when the signed-in person created this key. */
  created_by_me?: boolean;
  /** Capability scopes. */
  scopes?: string[] | null;
}

export async function fetchApiKeys(): Promise<{ total: number; keys: ApiKeySummary[] }> {
  return http<{ total: number; keys: ApiKeySummary[] }>("/api/api-keys");
}

// ── OAuth connections for the MCP server (routes/oauth-server.ts) ────────────

export type McpAccessLevelId = "read" | "ask" | "write";

export interface OAuthConsentView {
  client: { name: string; host: string; loopback: boolean };
  redirect_host: string;
  requested_access: McpAccessLevelId;
  workspace: { id: string; name: string | null };
  can_approve: boolean;
  expires_at: string;
}

export async function fetchOAuthConsent(request: string): Promise<OAuthConsentView> {
  return http<OAuthConsentView>(`/api/mcp-auth/consent?request=${encodeURIComponent(request)}`);
}

/** Records the decision. */
export async function decideOAuthConsent(
  request: string,
  decision: "approve" | "deny",
  access?: McpAccessLevelId,
): Promise<{ redirect_to: string }> {
  return http<{ redirect_to: string }>("/api/mcp-auth/consent", {
    method: "POST",
    body: JSON.stringify(access ? { request, decision, access } : { request, decision }),
  });
}

export interface OAuthGrantSummary {
  id: string;
  client_name: string;
  client_host: string;
  access: McpAccessLevelId;
  created_at: string;
  last_used_at: string | null;
  created_by_me: boolean;
}

export async function fetchOAuthGrants(): Promise<{ total: number; grants: OAuthGrantSummary[] }> {
  return http<{ total: number; grants: OAuthGrantSummary[] }>("/api/mcp-auth/grants");
}

/** Disconnects an assistant. Its tokens stop working on their next use. */
export async function revokeOAuthGrant(id: string): Promise<void> {
  await http(`/api/mcp-auth/grants/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Revokes a key. It stops working on its next request. */
export async function revokeApiKey(id: string): Promise<void> {
  await http(`/api/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function createApiKey(label: string, scopes?: string[]): Promise<ApiKeySummary & { plaintext: string }> {
  return http<ApiKeySummary & { plaintext: string }>("/api/api-keys", {
    method: "POST",
    body: JSON.stringify(scopes ? { label, scopes } : { label }),
  });
}

export interface WorkspaceMembership {
  workspace_id: string;
  name: string;
  alias?: string;
  role: "owner" | "admin" | "member" | "analyst" | "partner_reviewer";
  is_home: boolean;
  joined_at: string;
}

export interface WorkspaceListResponse {
  user_id: string;
  active_workspace_id: string;
  workspaces: WorkspaceMembership[];
}

export async function fetchWorkspaces(): Promise<WorkspaceListResponse> {
  return http<WorkspaceListResponse>("/api/auth/workspaces");
}

export async function switchWorkspace(
  workspace_id: string,
): Promise<{ active_workspace_id: string }> {
  return http<{ active_workspace_id: string }>("/api/auth/switch-workspace", {
    method: "POST",
    body: JSON.stringify({ workspace_id }),
  });
}

export async function createWorkspace(input: {
  name: string;
  stage?: "preseed" | "seriesab" | "growth" | "pe";
}): Promise<{ active_workspace_id: string; workspace: WorkspaceMembership }> {
  return http<{ active_workspace_id: string; workspace: WorkspaceMembership }>(
    "/api/auth/workspaces",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export interface WorkspaceInvitation {
  id: string;
  invited_email: string;
  role: "owner" | "admin" | "member" | "analyst" | "partner_reviewer" | string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  /** HRD-1-S06 T7: companies granted to an analyst or partner reviewer at invite time. */
  case_ids?: string[] | null;
  dimension_keys?: string[] | null;
}

/** Scope an analyst or partner reviewer invite carries (HRD-1-S06 T7). */
export interface WorkspaceInviteScope {
  case_ids?: string[];
  dimension_keys?: string[];
}

export interface WorkspaceInvitationsResponse {
  total: number;
  invitations: WorkspaceInvitation[];
}

export async function fetchWorkspaceInvitations(): Promise<WorkspaceInvitationsResponse> {
  return http<WorkspaceInvitationsResponse>("/api/workspace/invitations");
}

export async function createWorkspaceInvitation(
  invited_email: string,
  role: "admin" | "member" | "analyst" | "partner_reviewer" = "member",
  scope?: WorkspaceInviteScope,
): Promise<
  WorkspaceInvitation & {
    token: string;
    email_status?: "sent" | "skipped" | "failed";
    email_error?: string;
  }
> {
  return http<
    WorkspaceInvitation & {
      token: string;
      email_status?: "sent" | "skipped" | "failed";
      email_error?: string;
    }
  >("/api/workspace/invitations", {
    method: "POST",
    body: JSON.stringify({
      invited_email,
      role,
      ...(scope?.case_ids ? { case_ids: scope.case_ids } : {}),
      ...(scope?.dimension_keys ? { dimension_keys: scope.dimension_keys } : {}),
    }),
  });
}

/** HRD-1-S06 T9: cancel a pending team invite; its link stops joining the workspace. */
export async function revokeWorkspaceInvitation(id: string): Promise<void> {
  await http<void>(`/api/workspace/invitations/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export type WorkspaceTeamRole = 'owner' | 'admin' | 'member' | 'analyst' | 'partner_reviewer';
export interface WorkspaceMember {
  id: string;
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: WorkspaceTeamRole;
  joined_at: string;
  case_ids: string[] | null;
  dimension_keys: string[] | null;
  virtual?: boolean;
}
export async function fetchWorkspaceMembers(): Promise<{ members: WorkspaceMember[] }> {
  return http<{ members: WorkspaceMember[] }>('/api/workspace/members');
}
export async function updateWorkspaceMember(
  id: string,
  update: Partial<Pick<WorkspaceMember, 'role' | 'case_ids' | 'dimension_keys'>>,
): Promise<WorkspaceMember> {
  return http<WorkspaceMember>(`/api/workspace/members/${encodeURIComponent(id)}`, {
    method: 'PUT', body: JSON.stringify(update),
  });
}

export interface WorkspaceMemberRemoval {
  removed: true;
  member_id: string;
  user_id: string;
  sessions_ended: number;
  api_keys_revoked: number;
}

/** HRD-1-S06 T6: remove a member; they lose access on their next request. */
export async function removeWorkspaceMember(id: string): Promise<WorkspaceMemberRemoval> {
  return http<WorkspaceMemberRemoval>(`/api/workspace/members/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function acceptWorkspaceInvitation(
  token: string,
): Promise<{ workspace_id: string; role: string }> {
  return http<{ workspace_id: string; role: string }>(
    "/api/workspace/invitations/accept",
    {
      method: "POST",
      body: JSON.stringify({ token }),
    },
  );
}

export interface AuditLogEntry {
  id: string;
  workspace_id: string;
  user_id: string | null;
  method: string;
  path: string;
  resource_type?: string;
  resource_id?: string;
  status_code: number;
  occurred_at: string;
  meta?: Record<string, string | number | boolean>;
}

export interface AuditLogResponse {
  workspace_id: string;
  total: number;
  entries: AuditLogEntry[];
}

export async function fetchAuditLog(
  limit = 25,
  options?: { path_prefix?: string; resource_type?: string; resource_id?: string },
): Promise<AuditLogResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (options?.path_prefix) params.set("path_prefix", options.path_prefix);
  if (options?.resource_type) params.set("resource_type", options.resource_type);
  if (options?.resource_id) params.set("resource_id", options.resource_id);
  return http<AuditLogResponse>(`/api/admin/audit-log?${params.toString()}`);
}

export interface WorkspacePlanSnapshot {
  workspace_id: string;
  name: string;
  mode: "investor" | "operator";
  plan: PlanId;
  billing_state?: "past_due" | "dunned_off";
  has_stripe_subscription: boolean;
  plan_grant?: {
    plan: PlanId;
    reason: string;
    granted_by_user_id: string;
    granted_at: string;
  };
  grantable_plans: PlanId[];
  /** What entitlements actually report. */
  effective_plan: PlanId | null;
  scored_cases_unlimited: boolean | null;
}

export interface WorkspacePlanGrantResult {
  workspace_id: string;
  plan_before: PlanId;
  plan_after: PlanId;
  billing_state_before?: "past_due" | "dunned_off";
  billing_state_after?: "past_due" | "dunned_off";
  granted_at: string;
  granted_by_user_id: string;
  reason: string;
  effective_plan: PlanId | null;
  scored_cases_unlimited: boolean | null;
  audit_resource_type: string;
}

export async function fetchWorkspacePlan(workspace_id: string): Promise<WorkspacePlanSnapshot> {
  return http<WorkspacePlanSnapshot>(
    `/api/admin/workspaces/${encodeURIComponent(workspace_id)}/plan`,
  );
}

export async function grantWorkspacePlan(input: {
  workspace_id: string;
  plan: PlanId;
  reason: string;
}): Promise<WorkspacePlanGrantResult> {
  return http<WorkspacePlanGrantResult>(
    `/api/admin/workspaces/${encodeURIComponent(input.workspace_id)}/plan`,
    { method: "POST", body: JSON.stringify({ plan: input.plan, reason: input.reason }) },
  );
}

/** Explicit revoke uses the report's observed grant timestamp so an old report cannot clear a newer manual grant. */
export async function revokeWorkspacePlanGrant(input: {
  workspace_id: string;
  granted_at: string;
  reason: string;
}): Promise<WorkspacePlanGrantResult> {
  return http<WorkspacePlanGrantResult>(
    `/api/admin/workspaces/${encodeURIComponent(input.workspace_id)}/plan-grant/revoke`,
    { method: "POST", body: JSON.stringify({ granted_at: input.granted_at, reason: input.reason }) },
  );
}

export interface PlanGrantStripeReconciliation {
  state: "none" | "available" | "unavailable" | "error";
  subscription_id: string | null;
  status: string | null;
  plan: PlanId | null;
  disagreement: boolean;
  detail?: string;
}

export interface ActivePlanGrantReportRow {
  workspace_id: string;
  workspace_name: string;
  granted_plan: PlanId;
  granted_at: string;
  reason: string;
  grantor: { user_id: string; email?: string; display_name?: string };
  stripe: PlanGrantStripeReconciliation;
}

export interface ActivePlanGrantReport {
  order: "oldest" | "newest";
  disagreements_only: boolean;
  total: number;
  grants: ActivePlanGrantReportRow[];
}

export async function fetchActivePlanGrantReport(options: {
  order: "oldest" | "newest";
  disagreements_only: boolean;
}): Promise<ActivePlanGrantReport> {
  const params = new URLSearchParams({
    order: options.order,
    disagreements_only: String(options.disagreements_only),
  });
  return http<ActivePlanGrantReport>(`/api/admin/plan-grants?${params.toString()}`);
}

// Staff user directory (GET /api/admin/users, POST /api/admin/users/:id/staff).

export interface AdminUserWorkspace {
  workspace_id: string;
  name: string;
  plan: PlanId;
  /** Workspace-scoped role. */
  role: "owner" | "admin" | "member" | "analyst" | "partner_reviewer" | null;
  mode: "investor" | "operator";
  is_home: boolean;
}

export interface AdminUserRow {
  user_id: string;
  email: string;
  display_name?: string;
  is_zoe_admin: boolean;
  created_at: string;
  workspaces: AdminUserWorkspace[];
}

export interface AdminUserSearchResponse {
  query: string;
  limit: number;
  total_matched: number;
  /** True when total_matched exceeded limit and `users` is a prefix of the matches. */
  truncated: boolean;
  users: AdminUserRow[];
}

export async function searchAdminUsers(q: string): Promise<AdminUserSearchResponse> {
  return http<AdminUserSearchResponse>(`/api/admin/users?q=${encodeURIComponent(q)}`);
}

export interface AdminStaffGrantResult {
  user_id: string;
  email: string;
  home_workspace_id?: string;
  is_zoe_admin_before: boolean;
  is_zoe_admin_after: boolean;
  /** The flag already held the requested value: 200, and nothing was written. */
  no_op: boolean;
  changed_at: string;
  changed_by_user_id: string;
  reason: string;
  audit_resource_type: string;
}

export async function setUserStaffStatus(input: {
  user_id: string;
  is_zoe_admin: boolean;
  reason: string;
}): Promise<AdminStaffGrantResult> {
  return http<AdminStaffGrantResult>(
    `/api/admin/users/${encodeURIComponent(input.user_id)}/staff`,
    {
      method: "POST",
      body: JSON.stringify({ is_zoe_admin: input.is_zoe_admin, reason: input.reason }),
    },
  );
}

export type ScoringConfigVersionStatus = "draft" | "published" | "archived";

export interface ScoringConfigVersion {
  id: string;
  version: string;
  label: string;
  status: ScoringConfigVersionStatus;
  active: boolean;
  base_version?: string;
  created_at: string;
  created_by_user_id?: string;
  updated_at: string;
  published_at?: string;
  published_by_user_id?: string;
}

export interface ScoringConfigVersionsResponse {
  active_version: string;
  versions: ScoringConfigVersion[];
}

export type ScoringDimension = Dimension;

export type ScoringStage = "preseed" | "seriesab" | "growth" | "pe";

export interface ScoringStageWeights {
  stage: ScoringStage;
  weights: Record<ScoringDimension, number>;
  source: "configured" | "default";
  updated_at?: string;
  updated_by_user_id?: string;
}

export interface ScoringStageWeightsResponse {
  version: string;
  editable: boolean;
  dimensions: ScoringDimension[];
  stages: ScoringStageWeights[];
}

export type ScoringBandDirection = "higher_is_better" | "lower_is_better";

export interface ScoringBandZone {
  label: string;
  threshold: number;
  score: number;
}

export interface ScoringBandMetric {
  metric_key: string;
  label: string;
  direction: ScoringBandDirection;
  source: "configured" | "default";
  zones: ScoringBandZone[];
  points: Array<[number, number]>;
  healthy_target?: string;
  v4_metric?: string;
  fidelity?: string;
  note?: string;
  updated_at?: string;
  updated_by_user_id?: string;
}

export interface ScoringBandsResponse {
  version: string;
  editable: boolean;
  metrics: ScoringBandMetric[];
}

export type ScoringTruthClass =
  | "real"
  | "conditional"
  | "proxy"
  | "decorative"
  | "dead";

export interface ScoringMetricRegistryRow {
  id: string;
  metric_key: string;
  label?: string;
  source_system?: string;
  dimensions: ScoringDimension[];
  truth_class: ScoringTruthClass;
  unit?: string;
  healthy_target?: string;
  scored?: boolean;
  points?: Array<[number, number]>;
  config_version?: string;
  updated_at: string;
  updated_by_user_id?: string;
}

export interface ScoringMetricsResponse {
  version: string;
  editable: boolean;
  dimensions: ScoringDimension[];
  truth_classes: ScoringTruthClass[];
  default_points: Array<[number, number]>;
  metrics: ScoringMetricRegistryRow[];
}

export interface RescoreSandboxDimensionDelta {
  dimension: ScoringDimension;
  label: string;
  active_score: number;
  draft_score: number;
  delta: number;
  active_confidence: number;
  draft_confidence: number;
  metric_count: number;
  metric_total: number;
  coverage_label: string;
}

export interface RescoreSandboxResponse {
  version: string;
  active_version: string;
  stage: ScoringStage;
  sample: {
    id: string;
    label: string;
    description: string;
    metrics_count: number;
    sources: string[];
  };
  active: {
    version: string;
    composite_score: number;
  };
  draft: {
    version: string;
    composite_score: number;
  };
  deltas: {
    composite_delta: number;
    changed_dimensions: number;
    dimensions: RescoreSandboxDimensionDelta[];
  };
}

export interface ScoringConfigDiffRow {
  kind: "stage_weight" | "band" | "metric";
  id: string;
  label: string;
  change: "added" | "changed" | "removed";
  before: unknown;
  after: unknown;
}

export interface ScoringConfigDiffResponse {
  version: string;
  base_version: string;
  summary: {
    total_changes: number;
    stage_weight_changes: number;
    band_changes: number;
    metric_changes: number;
  };
  changes: ScoringConfigDiffRow[];
}

export async function fetchScoringConfigVersions(): Promise<ScoringConfigVersionsResponse> {
  return http<ScoringConfigVersionsResponse>(
    "/api/admin/scoring-config/versions",
  );
}

export async function createScoringConfigDraft(input: {
  version?: string;
  label?: string;
  base_version?: string;
}): Promise<{ version: ScoringConfigVersion; active_version: string }> {
  return http<{ version: ScoringConfigVersion; active_version: string }>(
    "/api/admin/scoring-config/drafts",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function publishScoringConfigVersion(
  version: string,
): Promise<ScoringConfigVersionsResponse & { version: ScoringConfigVersion }> {
  return http<
    ScoringConfigVersionsResponse & { version: ScoringConfigVersion }
  >(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/publish`,
    { method: "POST" },
  );
}

export async function rollbackScoringConfigVersion(
  version: string,
): Promise<ScoringConfigVersionsResponse & { version: ScoringConfigVersion }> {
  return http<
    ScoringConfigVersionsResponse & { version: ScoringConfigVersion }
  >(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/rollback`,
    { method: "POST" },
  );
}

export async function fetchScoringConfigDiff(
  version: string,
  base?: string,
): Promise<ScoringConfigDiffResponse> {
  const params = new URLSearchParams();
  if (base) params.set("base", base);
  const query = params.toString();
  return http<ScoringConfigDiffResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/diff${query ? `?${query}` : ""}`,
  );
}

export async function fetchScoringStageWeights(
  version: string,
): Promise<ScoringStageWeightsResponse> {
  return http<ScoringStageWeightsResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/stage-weights`,
  );
}

export async function saveScoringStageWeights(
  version: string,
  stage: ScoringStage,
  weights: Record<ScoringDimension, number>,
): Promise<ScoringStageWeightsResponse & { stage: ScoringStageWeights }> {
  return http<ScoringStageWeightsResponse & { stage: ScoringStageWeights }>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/stage-weights/${encodeURIComponent(stage)}`,
    {
      method: "PUT",
      body: JSON.stringify({ weights }),
    },
  );
}

export interface ScoringConfidenceGate {
  auto_sign_pct: number;
  self_attest_pct: number;
  source: "configured" | "default";
  updated_at?: string;
  updated_by_user_id?: string;
}

export interface ScoringConfidenceGateResponse {
  version: string;
  editable: boolean;
  defaults: { auto_sign_pct: number; self_attest_pct: number };
  confidence_gate: ScoringConfidenceGate;
}

export async function fetchScoringConfidenceGate(
  version: string,
): Promise<ScoringConfidenceGateResponse> {
  return http<ScoringConfidenceGateResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/confidence-gate`,
  );
}

export interface ScoringCoverageTiersResponse {
  thresholds: Record<string, number>;
  tiers: Array<{ tier: string; min_coverage_pct: number }>;
  editable: boolean;
  measured_on: string;
}

export async function fetchScoringCoverageTiers(): Promise<ScoringCoverageTiersResponse> {
  return http<ScoringCoverageTiersResponse>("/api/admin/scoring-config/coverage-tiers");
}

export async function saveScoringConfidenceGate(
  version: string,
  thresholds: { auto_sign_pct: number; self_attest_pct: number },
): Promise<{ version: string; confidence_gate: ScoringConfidenceGate }> {
  return http<{ version: string; confidence_gate: ScoringConfidenceGate }>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/confidence-gate`,
    { method: "PUT", body: JSON.stringify(thresholds) },
  );
}

export async function fetchScoringBands(
  version: string,
): Promise<ScoringBandsResponse> {
  return http<ScoringBandsResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/bands`,
  );
}

export async function saveScoringBand(
  version: string,
  metricKey: string,
  points: Array<[number, number]>,
): Promise<ScoringBandsResponse & { metric: ScoringBandMetric }> {
  return http<ScoringBandsResponse & { metric: ScoringBandMetric }>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/bands/${encodeURIComponent(metricKey)}`,
    {
      method: "PUT",
      body: JSON.stringify({ points }),
    },
  );
}

export async function fetchScoringMetrics(
  version: string,
): Promise<ScoringMetricsResponse> {
  return http<ScoringMetricsResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/metrics`,
  );
}

export async function saveScoringMetric(
  version: string,
  metricKey: string,
  metric: {
    label?: string;
    source_system?: string;
    dimensions: ScoringDimension[];
    truth_class: ScoringTruthClass;
    unit?: string;
    healthy_target?: string;
    scored?: boolean;
    points?: Array<[number, number]>;
  },
): Promise<ScoringMetricsResponse & { metric: ScoringMetricRegistryRow }> {
  return http<ScoringMetricsResponse & { metric: ScoringMetricRegistryRow }>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/metrics/${encodeURIComponent(metricKey)}`,
    {
      method: "PUT",
      body: JSON.stringify(metric),
    },
  );
}

export async function fetchRescoreSandbox(
  version: string,
  stage: ScoringStage = "seriesab",
): Promise<RescoreSandboxResponse> {
  return http<RescoreSandboxResponse>(
    `/api/admin/scoring-config/versions/${encodeURIComponent(version)}/rescore-sandbox?stage=${encodeURIComponent(stage)}`,
  );
}

export async function markOnboardingComplete(): Promise<{
  id: string;
  onboarding_completed_at: string | null;
}> {
  return http<{ id: string; onboarding_completed_at: string | null }>(
    "/api/workspaces/me",
    {
      method: "PATCH",
      body: JSON.stringify({ onboarding_completed: true }),
    },
  );
}

export async function updateWorkspace(input: {
  name?: string;
  mode?: "investor" | "operator";
  stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?:
    | "SaaS"
    | "FinTech"
    | "HealthTech"
    | "ConsumerGoods"
    | "Services"
    | "Other"
    | null;
  headcount_band?: "1–25" | "26–100" | "101–500" | "500+" | null;
  company_url?: string | null;
}): Promise<{
  id: string;
  name: string;
  mode: "investor" | "operator";
  stage: "preseed" | "seriesab" | "growth" | "pe";
  sector: string | null;
  headcount_band: string | null;
  company_url: string | null;
  onboarding_completed_at: string | null;
}> {
  return http("/api/workspaces/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export const OPERATOR_COMPANY_SIZE_BANDS = [
  ['1-10', '1–10 people'],
  ['11-50', '11–50 people'],
  ['51-200', '51–200 people'],
  ['201-500', '201–500 people'],
  ['501-1000', '501–1,000 people'],
  ['1001+', '1,001+ people'],
] as const;

export interface OperatorWaitlistApplicationInput {
  full_name: string;
  work_email: string;
  company: string;
  role_title: string;
  company_website: string;
  company_size: (typeof OPERATOR_COMPANY_SIZE_BANDS)[number][0];
  help_request: string;
}

export interface OperatorWaitlistApplication {
  id: string;
  full_name: string;
  work_email: string;
  company: string;
  role_title: string;
  company_website: string;
  company_size: string;
  help_request: string;
  status: 'pending' | 'approved' | 'declined';
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by_user_id: string | null;
  redeemed_at: string | null;
  approval_reissued_at?: string | null;
}

export async function submitOperatorWaitlistApplication(input: OperatorWaitlistApplicationInput): Promise<{ status: 'received' }> {
  return http('/api/operator-waitlist/applications', { method: 'POST', body: JSON.stringify(input) });
}

export async function fetchOperatorWaitlistApplications(cursor?: string, status: OperatorWaitlistApplication['status'] = 'pending'): Promise<{ applications: OperatorWaitlistApplication[]; next_cursor: string | null }> {
  const query = new URLSearchParams({ status });
  if (cursor) query.set('cursor', cursor);
  return http(`/api/admin/operator-waitlist/applications?${query}`);
}

export async function decideOperatorWaitlistApplication(id: string, decision: 'approve' | 'decline' | 'reissue'): Promise<{ application: OperatorWaitlistApplication }> {
  return http(`/api/admin/operator-waitlist/applications/${encodeURIComponent(id)}/${decision}`, { method: 'POST', body: '{}' });
}

export async function redeemOperatorWaitlistApproval(): Promise<{ workspace_id: string; workspace_name: string }> {
  return http('/api/operator-waitlist/redeem', { method: 'POST', body: '{}' });
}

export async function signOut(): Promise<{ status: string }> {
  // Clear the local session regardless of how the server call resolves.
  try {
    return await http<{ status: string }>("/api/auth/signout", {
      method: "POST",
    });
  } finally {
    clearSessionToken();
    syncWorkspaceIdentity(null, null);
  }
}

/** ZPM-345 — spend a one-time hand-off code for this browser's session. */
export interface HandoffRedemption {
  status: string;
  /** The session the code handed over. Travels in this body, never in a URL. */
  session_id?: string;
}

export async function redeemHandoffCode(code: string): Promise<HandoffRedemption> {
  return http<HandoffRedemption>("/api/auth/handoff/redeem", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export interface SignOutEverywhereReceipt {
  status: string;
  /** "everywhere" only when the server actually resolved the account and ended every session. */
  scope: "everywhere" | "request";
  trusted_device_revoked: boolean;
}

export async function signOutEverywhere(): Promise<SignOutEverywhereReceipt> {
  try {
    return await http<SignOutEverywhereReceipt>("/api/auth/signout", {
      method: "POST",
      body: JSON.stringify({ everywhere: true }),
    });
  } finally {
    clearSessionToken();
    syncWorkspaceIdentity(null, null);
  }
}

/** Revoke every trusted device for this account and clear this browser's trusted-device cookie. */
export async function revokeTrustedDevices(): Promise<{ status: string }> {
  return http<{ status: string }>("/api/auth/trusted-devices", { method: "DELETE" });
}

export interface AccountDeleteReceipt {
  workspace_id: string;
  deleted_at: string;
  /** HRD-1-S05 T2: `partial` means something is still there; the UI never says "deleted" on it. */
  status?: "complete" | "partial";
  counts: Record<string, number>;
  failures?: Array<{ step: string; table?: string; reason: string }>;
  grants?: { attempted: number; revoked: number; failed: Array<{ rail: string; source: string; connection_id: string; reason: string }> };
  kept?: Array<{ table: string; reason: string; owner: string }>;
  retry?: { by: string; note: string } | null;
}

// Owner-only.
export async function deleteAccount(confirm: string): Promise<AccountDeleteReceipt> {
  const result = await http<AccountDeleteReceipt>("/api/account/delete", {
    method: "POST",
    body: JSON.stringify({ confirm }),
  });
  clearSessionToken();
  syncWorkspaceIdentity(null, null);
  return result;
}

export interface AccountDataExport {
  workspace_id: string;
  exported_at: string;
  status: "complete" | "partial";
  total_rows: number;
  sections: Record<string, unknown[]>;
  truncated_sections: string[];
  failed_sections: Array<{ name: string; reason: string }>;
  omitted_sections: Array<{ name: string; reason: string }>;
}

/** Owner-only. HRD-1-S05 T5: the whole workspace as one JSON document, credentials left out. */
export async function exportAccountData(): Promise<AccountDataExport> {
  return http<AccountDataExport>("/api/account/export", undefined, { timeoutMs: 120_000 });
}

export interface TwoFactorChallengeResponse {
  challenge_id: string;
  email: string;
  setup_required: boolean;
  otpauth_url: string | null;
  expires_at: string;
}

export interface TwoFactorVerifyResponse {
  status: "verified";
  session_id: string;
  /** Present ONLY when this verify completed an enrollment (or a rotation). */
  backup_codes?: string[];
  workspace?: MeWorkspace;
}

export type PasswordAuthResponse =
  | { status: "session_created"; session_id: string; workspace?: MeWorkspace }
  | {
      status: "challenge_required";
      challenge_id: string;
      workspace?: MeWorkspace;
    };

export async function signUpWithPassword(input: {
  email: string;
  password: string;
  name?: string;
  accepted_terms: boolean;
}): Promise<PasswordAuthResponse> {
  const result = await http<PasswordAuthResponse>(
    "/api/auth/password/sign-up",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  if (result.status === "session_created") noteSessionEstablished();
  return result;
}

export async function loginWithPassword(input: {
  email: string;
  password: string;
}): Promise<PasswordAuthResponse> {
  const result = await http<PasswordAuthResponse>("/api/auth/password/login", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (result.status === "session_created") noteSessionEstablished();
  return result;
}

export interface PasswordAuthStatusResponse {
  enabled: boolean;
}

export async function fetchPasswordAuthStatus(): Promise<PasswordAuthStatusResponse> {
  return http<PasswordAuthStatusResponse>("/api/auth/password/status");
}

/** True for the 410 the four retired password endpoints return. */
export function isPasswordAuthRetiredError(
  error: unknown,
): error is ApiError & { body: { error: "password_auth_retired" } } {
  return (
    error instanceof ApiError &&
    error.status === 410 &&
    typeof error.body === "object" &&
    error.body !== null &&
    "error" in error.body &&
    (error.body as { error?: unknown }).error === "password_auth_retired"
  );
}

export interface PasswordForgotResponse {
  status: "ok";
  detail: string;
}

// Enumeration-safe: the API returns the same success body whether or not the email has an account.
export async function requestPasswordReset(input: {
  email: string;
}): Promise<PasswordForgotResponse> {
  return http<PasswordForgotResponse>("/api/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface PasswordResetResponse {
  status: "reset";
}

export async function resetPassword(input: {
  token: string;
  password: string;
}): Promise<PasswordResetResponse> {
  return http<PasswordResetResponse>("/api/auth/password/reset", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// Magic-link sign-in ( step 8)

export interface MagicLinkRequestResponse {
  status: "ok";
  detail: string;
}

/** Enumeration-safe: the API returns the same success body whether or not the email has an account. */
export async function requestMagicLink(input: {
  email: string;
}): Promise<MagicLinkRequestResponse> {
  return http<MagicLinkRequestResponse>("/api/auth/magic-link/request", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** Work-email sign-up on the same rail. */
export async function requestMagicLinkSignup(input: {
  email: string;
  workspace_name?: string;
  display_name?: string;
  mode?: "investor" | "operator";
}): Promise<MagicLinkRequestResponse> {
  return http<MagicLinkRequestResponse>("/api/auth/magic-link/request", {
    method: "POST",
    body: JSON.stringify({
      email: input.email,
      intent: "signup",
      workspace_name: input.workspace_name,
      display_name: input.display_name,
      mode: input.mode ?? "investor",
      legal_accepted: true,
    }),
  });
}

/** Redeem an emailed sign-in link. */
export function isMagicLinkWrongDeviceError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    typeof error.body === "object" &&
    error.body !== null &&
    "error" in error.body &&
    (error.body as { error?: unknown }).error === "magic_link_wrong_device"
  );
}

/**
 * The link was opened in a browser other than the one that asked for it, and nothing was spent.
 * Returns the masked address the API answered with (`j•••@acme.com`), or null for any other error.
 */
export function magicLinkConfirmRequiredEmail(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as { status?: unknown; email?: unknown } | null;
  if (typeof body !== "object" || body === null || body.status !== "confirm_required") return null;
  return typeof body.email === "string" ? body.email : "";
}

/** `confirm: true` is the person's click on "Continue as …", needed only in another browser. */
export async function verifyMagicLink(input: {
  token: string;
  confirm?: boolean;
}): Promise<PasswordAuthResponse> {
  const result = await http<PasswordAuthResponse>(
    "/api/auth/magic-link/verify",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  if (result.status === "session_created") noteSessionEstablished();
  return result;
}

export interface RagDocumentListItem {
  lifecycle_status?: 'active' | 'quarantined' | 'deleted' | 'replaced';
  quarantine_reason?: string | null;
  document_kind?: 'deck' | 'data-room' | 'transcript' | 'question-evidence' | 'question-context' | null;
  question_attachment?: { diagnostic_id: string; claim_id: string } | null;
  question_context_text?: string | null;
  id: string;
  family_id: string;
  version: number;
  title: string | null;
  source: string;
  trust: string;
  observed_at: string | null;
  created_at: string;
  char_count: number;
  external_ref: string | null;
  citation_ref?: string;
}

export interface RagDocumentsResponse {
  documents: RagDocumentListItem[];
}

export interface RagIngestResponse {
  document_id: string;
  family_id: string;
  version: number;
  chunk_count: number;
  deduplicated: boolean;
  processing_status?: 'parsed' | 'quarantined';
  parser?: string;
  parser_version?: string;
  byte_hash?: string;
  locator_count?: number;
  warnings?: string[];
  quarantine_reason?: string;
}

export interface RagIngestFileMetadata {
  filename: string;
  content_type?: string;
  size_bytes?: number;
}

interface RagIngestDocumentInput {
  codename: string;
  title: string;
  text?: string;
  content_base64?: string;
  observed_at?: string;
  file_metadata?: RagIngestFileMetadata;
}

async function ingestDealRagDocument(
  input: RagIngestDocumentInput & { source: "upload" | "transcript"; document_kind: "deck" | "data-room" | "transcript" | "question-evidence" | "question-context"; question_attachment?: { diagnostic_id: string; claim_id: string } },
): Promise<RagIngestResponse> {
  return http<RagIngestResponse>("/api/rag/ingest", {
    method: "POST",
    body: JSON.stringify({
      ...(input.content_base64 !== undefined
        ? { content_base64: input.content_base64 }
        : { text: input.text }),
      source: input.source,
      trust: input.document_kind === "deck" || input.document_kind === "question-evidence" || input.document_kind === "question-context" ? "unverified" : "verified",
      ...(input.question_attachment ? { question_attachment: input.question_attachment } : {}),
      document_kind: input.document_kind,
      title: input.title,
      external_ref: dealExternalRef(input.codename),
      observed_at: input.observed_at,
      file_metadata: input.file_metadata,
    }),
  });
}

export function ingestQuestionEvidenceDocument(input: RagIngestDocumentInput & { diagnostic_id: string; question_id: string }): Promise<RagIngestResponse> {
  return ingestDealRagDocument({ ...input, source: 'upload', document_kind: 'question-evidence',
    question_attachment: { diagnostic_id: input.diagnostic_id, claim_id: input.question_id } });
}

export function ingestQuestionContext(input: { codename: string; diagnostic_id: string; question_id: string; text: string }): Promise<RagIngestResponse> {
  return ingestDealRagDocument({ ...input, title: 'Written context', source: 'upload', document_kind: 'question-context',
    question_attachment: { diagnostic_id: input.diagnostic_id, claim_id: input.question_id } });
}

export async function ingestDataRoomDocument(input: RagIngestDocumentInput): Promise<RagIngestResponse> {
  return ingestDealRagDocument({ ...input, source: "upload", document_kind: "data-room" });
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : "");
    };
    reader.onerror = () => reject(reader.error ?? new Error("file_read_failed"));
    reader.readAsDataURL(file);
  });
}

export interface RagIngestFileInput {
  codename: string;
  title: string;
  file: File;
  observed_at?: string;
}

export async function ingestDataRoomFile(input: RagIngestFileInput): Promise<RagIngestResponse> {
  const content_base64 = await fileToBase64(input.file);
  return http<RagIngestResponse>("/api/rag/ingest", {
    method: "POST",
    body: JSON.stringify({
      content_base64,
      file_metadata: {
        filename: input.file.name,
        content_type: input.file.type || undefined,
        size_bytes: input.file.size,
      },
      source: "upload",
      trust: "verified",
      title: input.title,
      external_ref: dealExternalRef(input.codename),
      observed_at: input.observed_at,
    }),
  });
}

export async function ingestDeckDocument(input: RagIngestDocumentInput): Promise<RagIngestResponse> {
  return ingestDealRagDocument({ ...input, source: "upload", document_kind: "deck" });
}

export async function ingestTranscriptDocument(input: RagIngestDocumentInput): Promise<RagIngestResponse> {
  return ingestDealRagDocument({ ...input, source: "transcript", document_kind: "transcript" });
}

export async function fetchRagDocuments(opts: { external_ref?: string; include_quarantined?: boolean } = {}): Promise<RagDocumentListItem[]> {
  const params = new URLSearchParams();
  if (opts.external_ref) params.set("external_ref", opts.external_ref);
  if (opts.include_quarantined) params.set("include_quarantined", "true");
  const res = await http<RagDocumentsResponse>(`/api/rag/documents${params.size ? `?${params}` : ""}`);
  return res.documents;
}

export interface CsvFallbackTemplateSummary {
  id: string;
  title: string;
  ask: string;
  summary: string;
  filename: string;
  download_path: string;
  columns: Array<{ name: string; describe: string }>;
}

export async function fetchCsvFallbackTemplates(): Promise<CsvFallbackTemplateSummary[]> {
  const res = await http<{ templates?: CsvFallbackTemplateSummary[] }>("/api/rag/templates");
  return Array.isArray(res.templates) ? res.templates : [];
}

export async function deleteRagDocument(documentId: string): Promise<{
  deleted: boolean;
  document_id: string;
  chunk_count: number;
  embedding_count: number;
}> {
  return http(`/api/rag/documents/${encodeURIComponent(documentId)}`, {
    method: "DELETE",
  });
}

export interface DataAccessArea {
  id: string;
  label: string;
  description: string;
  sources: string[];
}
export type DataAreaStatus = 'connected' | 'syncing' | 'needs_attention' | 'declined' | 'awaiting_company';
export async function fetchDataAccessAreas(): Promise<{ areas: DataAccessArea[] }> {
  return http('/api/data-access/areas');
}

export interface TargetInviteResponse {
  id: string;
  token: string;
  url: string;
  expires_at: string;
  subject_type: "pending_deal";
  deal_codename: string;
  requested_sources: string[];
  requested_areas?: string[];
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string;
  headcount_band?: string;
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  email_status: "sent" | "skipped" | "failed" | "not_requested";
  email_error?: string;
  /** True when this call issued a NEW link. */
  invite_minted?: boolean;
  /** The systems whose dead connection Zoe just asked the contact to reconnect on the new link. */
  reconnect_requested?: string[];
  request_tracker?: SourceRequestTrackerItem[];
}

export async function createTargetInvite(input: {
  codename: string;
  to_email?: string;
  recipient_name?: string;
  requested_sources?: string[];
  requested_areas?: string[];
  source_owners?: SourceOwnerInput[];
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string;
  headcount_band?: "1–25" | "26–100" | "101–500" | "500+";
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  note?: string;
}): Promise<TargetInviteResponse> {
  const { codename, ...body } = input;
  return http<TargetInviteResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/invite`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export interface OnboardingTargetDraftResponse {
  id: string;
  deal_codename: string;
  invite_status: "not_sent" | "link_created" | "email_sent" | "email_failed";
  requested_sources: string[];
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
}

export async function persistOnboardingTargetDraft(input: {
  codename: string;
  to_email?: string;
  recipient_name?: string;
  requested_sources: string[];
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  note?: string;
}): Promise<OnboardingTargetDraftResponse> {
  return http<OnboardingTargetDraftResponse>("/api/onboarding/target-draft", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface SourceOwnerInput {
  source: string;
  owner_name?: string;
  owner_email?: string;
}

export interface SourceRequestTrackerItem {
  source: string;
  owner_name?: string;
  owner_email?: string;
  invite_url: string;
  status: "asked" | "connected" | "declined";
  lifecycle_status?:
    | "created"
    | "sent"
    | "opened"
    | "connected"
    | "declined"
    | "expired"
    | "failed";
  asked_at: string;
  opened_at?: string;
  connected_at?: string;
  declined_at?: string;
  declined_reason?: string;
  /** ZMPP-45: the contact's last sign-in for this system came back without a connection. */
  connect_failed_at?: string;
  expires_at?: string;
  last_reminded_at?: string;
  reminder_count?: number;
  /** Ruling 13-A: a granted connection died and Zoe asked the company contact to sign in again. */
  /** The connection is dead AND the invite behind it is expired or revoked, so Zoe could not ask. */
  reconnect_state?: "reconnect_requested" | "still_disconnected" | "needs_new_request";
  reconnect_requested_at?: string;
  reconnect_reminded_at?: string;
  /** When Zoe found it dead with no live link to ask through. No mail went. */
  reconnect_blocked_at?: string;
  /** The link behind this row is revoked, expired or gone, so nobody can open it. */
  invite_dead?: boolean;
  /** The reconnect ask rode the access-request mail, so the contact got one mail rather than two. */
  reconnect_mail_combined?: boolean;
  email_status?: "sent" | "skipped" | "failed" | "not_requested";
}

/** Pre-flight coverage projection (apps/api/src/jobs/coverage-projection.ts). */
export interface ApiCoverageProjection {
  sources: string[];
  /** Named sources the registry knows of no scoreable metric for. */
  sources_without_scoreable_metrics: string[];
  ceiling_pct: number;
  floor_pct: number;
  headroom_pct: number;
  standing: "cannot_clear" | "fragile" | "clears";
  can_clear_floor: boolean;
  reachable_dimensions: string[];
  unreachable_dimensions: string[];
  per_source: Array<{ source: string; dimensions: string[]; weight_pct: number }>;
  blocked_sources: Array<{
    source: string;
    state: string;
    at_risk_dimensions: string[];
    at_risk_dimension_labels: string[];
    at_risk_pct: number;
    caveat: string;
  }>;
  redundant_families?: Array<{
    family: string;
    family_label: string;
    sources: string[];
    adds_pct: number;
    note: string;
  }>;
  gaps: Array<{
    source: string;
    adds_pct: number;
    dimensions: string[];
    dimension_labels: string[];
  }>;
  /** Deterministic prose for the whole projection, written on the API. */
  line: string;
  /** "Connect Slack to cover Culture & People and C-Suite." */
  next_step?: string;
}

/** What skipping one source costs this case, on the axis the signing gate reads. */
export interface ApiSkipCost {
  source: string;
  ceiling_with_pct: number;
  ceiling_without_pct: number;
  costs_pct: number;
  dimensions: string[];
  dimension_labels: string[];
  /** For a zero-cost skip: the sources that already reach the same dimensions. */
  covered_by: string[];
  line: string;
}

export interface TargetInviteStatusResponse {
  /** Advisory only: requesting access remains possible; Generate enforces the allowance. */
  scored_case_warning?: UpgradeRequiredBody | null;
  /** Canonical access-request history: absent means unavailable, [] means known empty. */
  request_lifecycle_statuses?: DealSummary['request_lifecycle_statuses'];
  request_link_status?: 'not_created' | 'active' | 'expired' | 'revoked';
  requested_areas?: string[];
  area_status?: Record<string, DataAreaStatus>;
  request_url?: string;
  request_expires_at?: string;
  request_note?: string;
  deal_codename: string;
  /** Legacy invitation-channel status, not proof of access-request history. */
  invite_status: "not_sent" | "link_created" | "email_sent" | "email_failed";
  /** `connected_no_selection` — connected, but nothing picked for it to read (GitHub with no repositories). */
  source_status: Record<
    string,
    "not_connected" | "connected" | "connected_no_selection" | "error" | "declined"
  >;
  requested_sources: string[];
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string;
  headcount_band?: string;
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  request_tracker: SourceRequestTrackerItem[];
  active_invite_count: number;
  /** Coverage gate the diagnostic-start endpoint enforces (typically 40). */
  required_coverage_pct?: number;
  /** This deal's live connected-source coverage against that gate. */
  provided_coverage_pct?: number;
  /** What these sources could reach on the axis the signing gate measures. */
  coverage_projection?: ApiCoverageProjection;
  /** What skipping each source would cost, keyed by source id. */
  skip_costs?: Record<string, ApiSkipCost>;
}

export async function fetchTargetInviteStatus(
  codename: string,
): Promise<TargetInviteStatusResponse> {
  return http<TargetInviteStatusResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/invite/status`,
  );
}

// Deal-scoped integration panel — the per-deal source register behind the Sources screen.
export type DealIntegrationSourceStatus =
  | "synced"
  | "syncing"
  | "declined"
  | "not_connected"
  | "needs_attention"
  | "files"
  // Had a live grant (real sync history) but no connection backs it right now.
  | "disconnected";

export interface DealIntegrationSource {
  source: string;
  label: string;
  detail: string;
  status: DealIntegrationSourceStatus;
  sync_percent?: number;
  file_count?: number;
  synced_at?: string;
  declined_at?: string;
  attention_reason?: string;
  /** Who filled this source — the investor's own connect, or a named source owner via invite. */
  filled_by?: "investor_self" | "target_owner";
  owner_email?: string | null;
  last_synced_at?: string | null;
  shared?: boolean;
  shared_with?: Array<{ deal_id: string | null; deal_codename: string | null; source: string }>;
  is_sandbox_clone?: boolean;
}

export interface DealIntegrationPanelResponse {
  deal_slug: string;
  sources: DealIntegrationSource[];
  last_run_at?: string;
  last_run_note?: string;
  /** Coverage gate the diagnostic-start endpoint enforces (typically 40). */
  required_coverage_pct?: number;
  /** This deal's live connected-source coverage against that gate. */
  provided_coverage_pct?: number;
  source_dimensions?: { source: string; label: string; dimensions: string[] }[];
}

export async function fetchDealIntegrationPanel(
  codename: string,
): Promise<DealIntegrationPanelResponse> {
  return http<DealIntegrationPanelResponse>(
    `/api/deals/${encodeURIComponent(codename)}/integration-panel`,
  );
}

export interface DiagnosticDimensionInput {
  dimension: string;
  metric_key: string;
  value: number;
  unit?: string;
  /** The value as every surface prints it ("$4,200,000", "75%", "14 months"). Server-decided; absent from an older API. */
  display_value?: string;
  source: string;
  observed_at: string;
  /** The words this reading's date prints as. Server-decided. */
  stated_date?: string;
  normalized_score: number;
  weight: number;
  confidence: number;
}

export interface DiagnosticDimensionInputsResponse {
  diagnostic_id: string;
  count: number;
  rows: DiagnosticDimensionInput[];
}

export async function fetchDiagnosticDimensionInputs(
  diagnosticId: string,
): Promise<DiagnosticDimensionInputsResponse> {
  return http<DiagnosticDimensionInputsResponse>(
    `/api/diagnostics/${encodeURIComponent(diagnosticId)}/dimension-inputs`,
  );
}

export interface DiagnosticReportVersion {
  id: string;
  version: number;
  reason: string;
  parent_report_id: string | null;
  scoring_config_version: string;
  composite_score: number | null;
  readiness?: import('@/types/report').ReportData['readiness'];
  created_at: string;
}

export interface DiagnosticReportVersionsResponse {
  versions: DiagnosticReportVersion[];
}

export async function fetchDiagnosticReportVersions(
  diagnosticId: string,
): Promise<DiagnosticReportVersionsResponse> {
  return http<DiagnosticReportVersionsResponse>(
    `/api/diagnostics/${encodeURIComponent(diagnosticId)}/versions`,
  );
}

export interface TargetInviteReminderResponse {
  deal_codename: string;
  reminded_count: number;
  skipped_count: number;
  request_tracker: SourceRequestTrackerItem[];
}

export async function remindTargetInviteOwners(
  codename: string,
): Promise<TargetInviteReminderResponse> {
  return http<TargetInviteReminderResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/invite/remind`,
    { method: "POST" },
  );
}

export interface RevokeTargetInviteResponse {
  revoked_count: number;
  revoked_connection_count: number;
}

/** ZMPP-22: the investor ends the company's access. The link stops working and every system connected through it stops feeding Reads. */
export async function revokeTargetInvite(codename: string): Promise<RevokeTargetInviteResponse> {
  return http<RevokeTargetInviteResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/invite/revoke`,
    { method: "POST" },
  );
}

export interface ReopenDealSourceResponse {
  deal_codename: string;
  source: string;
  status: "asked";
  request_tracker: SourceRequestTrackerItem[];
}

export async function reopenDeclinedDealSource(
  codename: string,
  source: string,
): Promise<ReopenDealSourceResponse> {
  return http<ReopenDealSourceResponse>(
    `/api/deals/pipeline/${encodeURIComponent(codename)}/invite/reconsider/${encodeURIComponent(source)}`,
    { method: "POST" },
  );
}

export interface PublicInviteStatusResponse {
  area_status?: Record<string, DataAreaStatus>;
  codename: string;
  subject_type: "diagnostic" | "pending_deal";
  diagnostic_ready: boolean;
  status: "invited" | "connecting" | "connected" | "declined" | "unavailable";
  /** Requested sources that cannot be connected from this link and are not already connected. */
  unavailable_sources?: string[];
  sources: Record<
    string,
    "not_connected" | "connected" | "syncing" | "connected_no_selection" | "declined" | "error"
  >;
  /** Immutable timestamps recording the contact's authorization or decline. */
  source_events?: Record<string, { connected_at?: string; declined_at?: string }>;
  source_health?: Record<string, string>;
  /** What the provider side did when this contact disconnected a source. */
  source_revocation?: Record<
    string,
    { revoked_provider_side: boolean; reason?: string | null; at?: string }
  >;
  /** Coverage gate the diagnostic-start endpoint enforces (typically 40). */
  required_coverage_pct?: number;
  /** This deal's live connected-source coverage against that gate. */
  provided_coverage_pct?: number;
  /** What these sources could reach on the axis the signing gate measures. */
  coverage_projection?: ApiCoverageProjection;
  /** What skipping each source would cost, keyed by source id. */
  skip_costs?: Record<string, ApiSkipCost>;
  consent?: PublicInviteConsent;
  /** ZMPP-22 T7: sources paused until this contact accepts the current terms. */
  source_consent_paused?: string[];
}

export async function fetchPublicInviteStatus(
  token: string,
): Promise<PublicInviteStatusResponse> {
  return http<PublicInviteStatusResponse>(
    `/api/invites/${capabilityTokenPathSegment(token)}/status`,
    inviteInit(token),
  );
}

export interface PublicInvitePayload {
  requested_areas?: string[];
  data_access_areas?: DataAccessArea[];
  request_note?: string;
  expires_at?: string;
  codename: string;
  subject_type: "diagnostic" | "pending_deal";
  /** The sources the case asked for — the emphasized rows. */
  sources_required: string[];
  sources_supported?: string[];
  install_urls?: Record<string, string>;
  unavailable_sources?: string[];
  oauth_init_urls: Record<string, string>;
  status_url?: string;
  consent?: PublicInviteConsent;
  consent_url?: string;
  /** `contact` for the emailed person, `read_only` for anyone else, `open` when no contact was named. */
  access?: "contact" | "read_only" | "open";
  requesting_workspace_member?: boolean;
  /** Masked address of the person this request was sent to, e.g. d***@acme.com. */
  contact_hint?: string;
  /** Who is asking: the requesting firm's name, else the requester's own name. */
  requested_by_firm?: string;
  /** The requester's display name. Absent on older API responses. */
  requested_by_name?: string;
  /** The requester's email. */
  requested_by_email?: string;
}

export async function delegateInvite(token: string, email: string): Promise<{ delegated_to: string; email_status: string }> {
  return http(`/api/invites/${capabilityTokenPathSegment(token)}/delegate`, inviteInit(token, { method: "POST", body: JSON.stringify({ email }) }));
}

export async function requestOwnInviteLink(token: string): Promise<{ requested: boolean }> {
  return http(`/api/invites/${capabilityTokenPathSegment(token)}/request-link`, inviteInit(token, { method: "POST", body: "{}" }));
}

export interface PublicInviteConsent {
  accepted_at: string | null;
  dpa_version: string;
  dpa_url: string | null;
  trust_summary_url: string | null;
  /** True while legal has not yet delivered the formal DPA; the inline terms apply. */
  dpa_document_pending: boolean;
  inline_terms: string[];
}

/** Record the owner's acceptance of the data-processing terms as a signed consent event on the invite. */
export async function acceptInviteConsent(
  token: string,
  dpa_version: string,
): Promise<{ consent: PublicInviteConsent; created: boolean }> {
  return http(`/api/invites/${capabilityTokenPathSegment(token)}/consent`, inviteInit(token, {
    method: "POST",
    body: JSON.stringify({ dpa_version }),
  }));
}

/** Public invite payload (codename + per-source connect URLs). */
export async function fetchPublicInvite(
  token: string,
): Promise<PublicInvitePayload> {
  return http<PublicInvitePayload>(`/api/invites/${capabilityTokenPathSegment(token)}`, inviteInit(token));
}

export const DECLINE_REASON_MAX_LENGTH = 280;

export async function declineInviteSource(
  token: string,
  source: string,
  reason?: string,
): Promise<PublicInviteStatusResponse> {
  const trimmed = (reason ?? "").trim().slice(0, DECLINE_REASON_MAX_LENGTH);
  return http<PublicInviteStatusResponse>(
    `/api/invites/${capabilityTokenPathSegment(token)}/decline/${encodeURIComponent(source)}`,
    inviteInit(token, {
      method: "POST",
      ...(trimmed ? { body: JSON.stringify({ reason: trimmed }) } : {}),
    }),
  );
}

/** Reopen a previously deferred/declined source so the owner can connect after all. */
export async function reconsiderInviteSource(
  token: string,
  source: string,
): Promise<PublicInviteStatusResponse> {
  return http<PublicInviteStatusResponse>(
    `/api/invites/${capabilityTokenPathSegment(token)}/reconsider/${encodeURIComponent(source)}`,
    inviteInit(token, { method: "POST" }),
  );
}

/** Disconnect a source the owner previously connected, revoking the read-only grant. */
export async function disconnectInviteSource(
  token: string,
  source: string,
): Promise<PublicInviteStatusResponse> {
  return http<PublicInviteStatusResponse>(
    `/api/invites/${capabilityTokenPathSegment(token)}/disconnect/${encodeURIComponent(source)}`,
    inviteInit(token, { method: "POST" }),
  );
}

export async function fetchTwoFactorChallenge(
  challenge: string,
): Promise<TwoFactorChallengeResponse> {
  return http<TwoFactorChallengeResponse>(
    `/api/auth/2fa/challenge?challenge=${encodeURIComponent(challenge)}`,
  );
}

export async function verifyTwoFactorChallenge(
  challenge_id: string,
  code: string,
  trust_device: boolean,
): Promise<TwoFactorVerifyResponse> {
  const result = await http<TwoFactorVerifyResponse>("/api/auth/2fa/verify", {
    method: "POST",
    body: JSON.stringify({ challenge_id, code, trust_device }),
  });
  noteSessionEstablished();
  return result;
}

export interface TwoFactorStatus {
  enabled: boolean;
  enrolled_at: string | null;
  backup_codes_remaining: number;
  /** Whether the server compels enrollment (ZOE_2FA_ENFORCEMENT_ENABLED). */
  enforced?: boolean;
}

export async function fetchTwoFactorStatus(): Promise<TwoFactorStatus> {
  return http<TwoFactorStatus>("/api/auth/2fa/status");
}

export async function startTwoFactorEnroll(): Promise<{ otpauth_url: string }> {
  return http<{ otpauth_url: string }>("/api/auth/2fa/enroll/start", { method: "POST" });
}

export async function confirmTwoFactorEnroll(code: string): Promise<{ status: "enabled"; backup_codes: string[] }> {
  return http<{ status: "enabled"; backup_codes: string[] }>("/api/auth/2fa/enroll/confirm", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

/** Replace the authenticator. */
export async function rotateTwoFactor(code: string): Promise<{ status: "rotating"; challenge_id: string }> {
  return http<{ status: "rotating"; challenge_id: string }>("/api/auth/2fa/rotate", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export async function regenerateBackupCodes(code: string): Promise<{ backup_codes: string[] }> {
  return http<{ backup_codes: string[] }>("/api/auth/2fa/backup-codes/regenerate", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export interface SessionProbe {
  authed: boolean;
  user?: { id: string; email?: string; role?: string } | null;
}

export async function checkSession(): Promise<SessionProbe> {
  return http<SessionProbe>("/api/auth/session");
}

export interface BillingPlan {
  id: "deal" | "desk" | "portfolio";
  name: string;
  monthly_usd: number | "contact";
  interval: "month" | "one_time" | "contact";
  features: string[];
  stripe_price_id_env_var: string;
  description?: string;
  name_source?: "stripe" | "catalog";
}

export async function fetchBillingPlans(
  mode: WorkspaceMode = getStoredWorkspaceMode(),
): Promise<{ plans: BillingPlan[] }> {
  // The API serves persona-aware naming so operator workspaces never read "Investor"/"cases".
  const query = mode === "operator" ? "?mode=operator" : "";
  return http<{ plans: BillingPlan[] }>(`/api/billing/plans${query}`);
}

export interface BillingEntitlements {
  workspace_id: string;
  plan: PlanId;
  policy: {
    id: "investor_hard_paywall";
    hard_paywall: "enforced" | "auth_first";
    owner_decision: "enforce_now";
    default_behavior: string;
    ga_gate: string;
  };
  entitlements: {
    can_create_diagnostic: boolean;
    can_rerun_diagnostic: boolean;
    can_view_report: boolean;
    can_use_billing_checkout: boolean;
  };
  upgrade_required: boolean;
  seam: string;
  /** Live-case count, compatibility only — no plan caps live cases any more (Drafts and Actives are unlimited). */
  cases?: {
    live: number;
    limit: number | null;
    unlimited: boolean;
    remaining: number | null;
  };
  /** Scored-case allowance — what a package limits (Free and Deal: one; Desk and Portfolio: unlimited). */
  scored_cases?: {
    used: number;
    limit: number | null;
    unlimited: boolean;
    remaining: number | null;
  };
}

export async function fetchBillingEntitlements(): Promise<BillingEntitlements> {
  return http<BillingEntitlements>("/api/billing/entitlements");
}

export interface PendingConversion {
  /** The Stripe PaymentIntent id. All three ad platforms deduplicate on it. */
  transaction_id: string;
  /** What Stripe actually took, in the currency's major unit. */
  value: number;
  currency: string;
  plan: string;
}

export async function fetchPendingConversion(): Promise<PendingConversion | null> {
  const body = await http<{ conversion: PendingConversion | null }>("/api/conversions/pending");
  return body.conversion ?? null;
}

export async function acknowledgeConversion(
  transaction_id: string,
): Promise<{ reported: boolean; already_reported: boolean }> {
  return http<{ reported: boolean; already_reported: boolean }>(
    `/api/conversions/${encodeURIComponent(transaction_id)}/reported`,
    { method: "POST" },
  );
}

export interface UsageSummary {
  workspace_id: string;
  plan: PlanId;
  counts: {
    diagnostics_last_30d: number;
    /** Reads that drew on the rolling allowance, including same-company reruns. */
    reads_run_last_30d?: number;
    /** Ticket 02 — reads that ended as a platform failure. */
    reads_failed_last_30d?: number;
    connections: number;
    api_keys_active: number;
    webhooks_active: number;
  };
  soft_caps: {
    diagnostics_per_month: number | null;
    connections: number | null;
    api_keys: number | null;
    webhooks: number | null;
  };
}

export async function fetchUsageSummary(): Promise<UsageSummary> {
  return http<UsageSummary>("/api/usage/summary");
}

export async function startCheckout(
  plan_id: string,
  return_url?: string,
): Promise<{ url?: string; session_id?: string; error?: string; detail?: string }> {
  return http<{ url?: string; session_id?: string }>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ plan_id, return_url }),
  });
}

export async function confirmCheckout(session_id: string): Promise<{
  status: "confirmed";
  workspace_id: string;
  plan: string;
  deal_count: number;
  amount_usd: number;
  /** HRD-1-S08 T4: a repeat Deal raised the scored-company allowance instead of moving the plan. */
  credit_granted?: boolean;
  scored_company_allowance?: number;
  /** True only for a legacy client that did not opt into recovery-only 503s. */
  analytics_pending?: true;
}> {
  return http("/api/billing/checkout/confirm", {
    method: "POST",
    body: JSON.stringify({ session_id, analytics_recovery: true }),
  });
}

export async function openCustomerPortal(
  return_url?: string,
): Promise<{ url?: string }> {
  return http<{ url?: string }>("/api/billing/portal", {
    method: "POST",
    body: JSON.stringify({ return_url }),
  });
}

export interface BackendInsight {
  id: string;
  kind: InsightKind;
  severity: "ok" | "attn" | "crit";
  portco_slug: string;
  portco_name: string;
  claim: string;
  evidence_label: string;
  strength: number;
  dimension?: string;
  case_url?: string;
  detected_at: string;
}

export type InsightKind =
  | "velocity_drop"
  | "sentiment_spike"
  | "burn_multiple"
  | "cash_threshold"
  | "vendor_spend_anomaly"
  | "ar_overdue";

export type InsightSeverity = "all" | "ok" | "attn" | "crit";
export type InsightStatus = "all" | "open" | "resolved";

export interface WorkspaceInsightRow {
  id: string;
  portco_slug: string;
  kind: InsightKind;
  severity: "ok" | "attn" | "crit";
  claim: string;
  evidence_label: string;
  detected_at: string;
  expires_at: string;
  resolution_status: string | null;
  resolved_at: string | null;
}

export interface WorkspaceInsightsResponse {
  workspace_id: string;
  generated_at: string;
  severity: InsightSeverity;
  status: InsightStatus;
  kind: InsightKind | "all";
  portco_slug: string | null;
  limit: number;
  offset: number;
  total_matching: number;
  rows: WorkspaceInsightRow[];
}

export interface InsightsTodayResponse {
  source: "fixture" | "live";
  insights: BackendInsight[];
}

export type ActionProposalSource = "finding" | "tension" | "alert";
export type ActionProposalArtifactType = "board_update" | "ic_memo" | "100_day_plan";

export interface ActionProposalArtifactSection {
  heading: string;
  body: string;
  evidence_ref?: string;
}

export interface ActionProposalArtifactDraft {
  source: "artifact_drafter";
  artifact_type: ActionProposalArtifactType;
  status: "draft";
  approval_state: "queued";
  delivery_state: "not_sent";
  generated_at: string;
  title: string;
  summary: string;
  sections: ActionProposalArtifactSection[];
}

export interface ActionProposalAutonomyAction {
  id: "review_draft" | "draft_founder_followup" | "approve_founder_followup" | "ingest_founder_answer";
  label: string;
  state: "available" | "locked";
  gate: string;
  audit_event: string;
}

export interface ActionProposalAutonomyControls {
  mode: "human_approval_required";
  auto_send_locked: true;
  audit_required: true;
  summary: string;
  gated_actions: ActionProposalAutonomyAction[];
}

export interface ActionProposalScenario {
  id: 'cut_burn_10' | 'cut_burn_20' | 'bridge_3_months';
  title: string;
  lever_label: string;
  baseline_runway_months: number | null;
  scenario_runway_months: number | null;
  delta_months: number | null;
  cash_flow_positive: boolean;
  baseline_cash_flow_positive: boolean;
}

export interface ActionProposalScenarioPack {
  source: 'scenario_engine';
  trigger_kind: 'burn_multiple' | 'cash_threshold';
  portco_slug: string;
  generated_at: string;
  baseline: {
    current_cash_usd: number;
    monthly_burn_usd: number;
    monthly_revenue_usd: number;
    cash_observed_at: string | null;
    revenue_observed_at: string | null;
  };
  scenarios: ActionProposalScenario[];
}

export interface ActionProposal {
  id: string;
  source: ActionProposalSource;
  source_ref: string;
  status: "draft";
  approval_state: "queued";
  delivery_state: "not_sent";
  auto_send: false;
  priority: "high" | "medium" | "low";
  title: string;
  detail: string;
  proposed_action: string;
  href: string;
  created_at: string;
  autonomy_controls: ActionProposalAutonomyControls;
  scenario_pack?: ActionProposalScenarioPack;
  artifact_draft?: ActionProposalArtifactDraft;
}

export interface ActionProposalsResponse {
  generated_at: string;
  total: number;
  proposals: ActionProposal[];
}

export async function fetchInsightsToday(): Promise<InsightsTodayResponse> {
  return http<InsightsTodayResponse>("/api/insights/today");
}

export async function fetchPortcoInsights(slug: string): Promise<InsightsTodayResponse> {
  return http<InsightsTodayResponse>(`/api/insights/portco/${encodeURIComponent(slug)}`);
}

export type FinancialAlertKind =
  | 'velocity_drop'
  | 'cash_threshold'
  | 'vendor_spend_anomaly'
  | 'ar_overdue'
  | 'key_person_flight_risk';

export interface FinancialAlertConfig {
  kind: FinancialAlertKind;
  value: number;
  unit: '%' | 'USD' | 'sigma' | 'days' | 'score';
  label: string;
  rationale: string;
  source: 'default' | 'override';
}

/** Thresholds exist only for a company marked for watching; any other company answers 404, which means none. */
export async function fetchFinancialAlertConfig(slug: string): Promise<FinancialAlertConfig[]> {
  try {
    const response = await http<{ alerts: FinancialAlertConfig[] }>(`/api/portcos/${encodeURIComponent(slug)}/financial-alerts`);
    return response.alerts;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return [];
    throw error;
  }
}

export async function saveFinancialAlertConfig(slug: string, kind: FinancialAlertKind, value: number): Promise<void> {
  await http(`/api/portcos/${encodeURIComponent(slug)}/financial-alerts`, {
    method: 'PUT',
    body: JSON.stringify({ kind, value }),
  });
}

export async function fetchActionProposals(opts: { limit?: number } = {}): Promise<ActionProposalsResponse> {
  const query = new URLSearchParams();
  if (opts.limit) query.set("limit", String(opts.limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return http<ActionProposalsResponse>(`/api/action-proposals${suffix}`);
}

export interface FounderFollowUp {
  id: string;
  workspace_id: string;
  proposal_id: string;
  source_ref: string;
  diagnostic_id?: string;
  status: "draft" | "share_link" | "sent" | "answered";
  approval_state: "draft" | "approved";
  delivery_state: "draft" | "share_link" | "sent";
  answer_state: "waiting" | "answered";
  question: string;
  draft_message: string;
  response_token: string;
  response_url: string;
  created_at: string;
  approved_at?: string;
  sent_at?: string;
  sent_to?: string;
  email_provider_id?: string;
  email_error?: string;
  email_skipped_reason?: string;
  share_link_ready_at?: string;
  answered_at?: string;
  answer_text?: string;
  rag_document_id?: string;
  report_updated_at?: string;
}

export interface PublicFounderFollowUp {
  source_ref: string;
  status: "share_link" | "sent" | "answered";
  answer_state: "waiting" | "answered";
  question: string;
  draft_message: string;
  response_url: string;
}

export async function fetchFounderFollowUps(): Promise<FounderFollowUp[]> {
  const body = await http<{ followups?: FounderFollowUp[] }>("/api/founder-followups");
  return Array.isArray(body.followups) ? body.followups : [];
}

export async function createFounderFollowUp(input: { proposal_id: string }): Promise<FounderFollowUp> {
  const body = await http<{ followup: FounderFollowUp }>("/api/founder-followups", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.followup;
}

export async function approveFounderFollowUp(id: string, input: { send_email?: boolean; recipient_email?: string; recipient_name?: string } = {}): Promise<FounderFollowUp> {
  const body = await http<{ followup: FounderFollowUp }>(`/api/founder-followups/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.followup;
}

export async function submitFounderFollowUpAnswer(token: string, answer_text: string): Promise<FounderFollowUp> {
  const body = await http<{ followup: FounderFollowUp }>(`/api/founder-followups/respond/${capabilityTokenPathSegment(token)}`, capabilityInit(token, {
    method: "POST",
    body: JSON.stringify({ answer_text }),
  }));
  return body.followup;
}

export async function fetchPublicFounderFollowUp(token: string): Promise<PublicFounderFollowUp> {
  const body = await http<{ followup: PublicFounderFollowUp }>(`/api/founder-followups/respond/${capabilityTokenPathSegment(token)}`, capabilityInit(token));
  return body.followup;
}

export async function fetchWorkspaceInsights(
  params: {
    severity?: InsightSeverity;
    status?: InsightStatus;
    kind?: InsightKind | "all";
    portco_slug?: string;
    limit?: number;
    offset?: number;
  } = {},
): Promise<WorkspaceInsightsResponse> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== "all")
      query.set(key, String(value));
  }
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return http<WorkspaceInsightsResponse>(`/api/insights${suffix}`);
}

interface ApiDimensionScore {
  dimension: string;
  score: number;
  confidence: number;
  metric_count?: number;
  metric_total?: number;
  coverage_fidelity?: "direct" | "mixed" | "proxy" | "none";
  coverage_label?: string;
}

interface ApiFinding {
  id: string;
  severity: FindingSeverity;
  dimension?: Dimension;
  claim: string;
  elaboration: string;
  observed_at?: string;
  evidence?: ApiFindingEvidence[];
}

interface ApiFindingEvidence {
  source: string;
  key: string;
  value: number;
  observed_at: string;
  normalized_score: number;
  unit?: string;
  period?: { label?: string; start?: string; end?: string; kind?: string; start_date?: string; end_date?: string };
  provenance?: {
    source_family?: string;
    extractor?: string;
    observed_at?: string;
    extraction_confidence?: number;
    locator?: string;
    external_id?: string;
    content_hash?: string;
    method?: 'api' | 'fetch' | 'browser_fallback' | 'document_parser' | 'csv_parser';
    /** The file this reading was read out of, when it came out of one. */
    document_id?: string;
    document_title?: string;
  };
  document_id?: string;
  record_id?: string;
  evidence_basis?: 'finding' | 'reading';
  /** The reading's customer name, server-decided (ZPM-428). Absent from an older API. */
  reading_name?: string;
  /** The value as every surface prints it ("$4,200,000"), server-decided (ZPM-428). Absent from an older API. */
  display_value?: string;
}

interface ApiSourceSummary {
  source: string;
  metric_count: number;
  error_count: number;
}

interface ApiDimensionEvidence {
  rows?: Array<{
    source: string;
    metric_key: string;
    value: string | number;
    confidence?: number;
    observed_at?: string;
    /** The words this reading's date prints as. Server-decided. */
    stated_date?: string;
    unit?: string;
    /** The file this reading was stated in, when it came out of one. */
    document_title?: string;
    locator?: string;
  }>;
  evidence_only_rows?: Array<{
    source: string;
    metric_key: string;
    value: string | number;
    confidence?: number;
    observed_at?: string;
    /** The words this reading's date prints as. Server-decided. */
    stated_date?: string;
    reason?: string;
    /** The file this reading was stated in, when it came out of one. */
    document_title?: string;
    locator?: string;
  }>;
  score_history?: number[];
  narrative?: string;
  missing_sources?: Array<{ source: string; reason: string }>;
  peer_benchmark?: {
    label: string;
    score: number;
    sample_size: number;
    percentile?: number;
    z_score?: number | null;
    source?: 'public_prior' | 'own_customer';
    own_customer_sample_size?: number;
  };
  predictive_signals?: Array<{
    metric_key: string;
    outcome: string;
    horizon_months: [number, number];
    evidence_basis: 'published_research' | 'retrospective_internal' | 'live_validated';
    validation_status: 'validated';
    claim: string;
    rationale: string;
  }>;
}

interface ApiVerificationGap {
  id: string;
  dimension?: string;
  claim: string;
  reason: string;
  data_gap: string;
  missing_sources?: string[];
  /** The per-dimension source sentence, derived from the scoring config. */
  source_sentence?: string;
  no_source_configured?: boolean;
  freshness_status?: "current" | "stale" | "unknown";
  citations?: Array<{ source: string; label: string; observed_at?: string; stated_date?: string; said?: { source_title: string; label: string; stated_date: string; claim?: string } }>;
}

interface ApiScoreCoverage {
  weight_pct: number;
  credible_dimensions: number;
  total_dimensions: number;
  missing_dimensions: string[];
}

interface ApiReadConfidence {
  confidence_pct?: number;
  decision?: string;
  thresholds?: { auto_sign_pct?: number; self_attest_pct?: number };
  coverage_tier?: string;
  coverage_pct?: number;
  warnings?: string[];
  requires_self_attestation?: boolean;
  inputs?: Array<{
    dimension?: string;
    label?: string;
    weight?: number;
    attainment?: number;
    contribution_pct?: number;
    attainable_contribution_pct?: number;
    assessed?: boolean;
  }>;
}

interface ApiPersistedReport {
  evidence_wiki?: ReportData['evidence_wiki'];
  company_investigation?: ReportData['company_investigation'];
  diagnostic_id: string;
  diagnostic_method?: 'unified_v1';
  composite_score: number | null;
  readiness?: import('@/types/report').ReportData['readiness'];
  read_confidence?: ApiReadConfidence;
  coverage_band?: string;
  partner_review?: PartnerReviewAccess;
  verdict_warnings?: string[];
  // Broken connectors behind this read, computed server-side.
  connector_warnings?: ReportConnectorWarning[];
  // How much of the 9-dimension scoring model composite_score is actually derived from.
  evidence_read?: import('@/types/report').EvidenceRead;
  company_read?: import('@/types/report').CompanyReadSnapshot;
  score_coverage?: ApiScoreCoverage;
  scorecard: ApiDimensionScore[];
  findings: ApiFinding[];
  letter_md: string | null;
  recommendations: Array<{
    heading: string;
    subheading?: string;
    items: string[];
    intent?: 'probe' | 'price_hedge' | 'walk' | 'monitor';
    basis?: 'finding' | 'verification_gap' | 'source_freshness' | 'fallback';
  }>;
  verdict_headline?: string;
  truth_status?: {
    verdict?: 'grounded' | 'fallback';
    recommendations?: 'grounded' | 'fallback';
    monitoring_plan?: 'grounded' | 'fallback';
    letter?: 'grounded' | 'fallback';
  };
  letter_template_fallback?: boolean;
  citation_manifest?: ReportData['citation_manifest'];
  scoring_config_version?: string;
  scoring_config_snapshot?: {
    version: string;
    captured_at?: string;
    stage_weights?: unknown[];
    scoring_bands?: unknown[];
    scoring_metrics?: Array<{ within_dimension_weight?: number }>;
  };
  monitoring_plan?: Array<{
    label: string;
    cadence: 'weekly' | 'monthly' | 'quarterly';
    reason: string;
    basis?: 'finding' | 'verification_gap' | 'source_freshness' | 'coverage';
  }>;
  dimension_evidence?: Record<string, ApiDimensionEvidence>;
  verification_gaps?: ApiVerificationGap[];
  sync_summary?: {
    source_count: number;
    coverage_pct: number;
    metrics_ingested?: number;
    sources?: ApiSourceSummary[];
  };
  data_synced_at?: string | null;
  case_context?: {
    deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
    funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
    sector?: string;
    headcount_band?: string;
    company_url?: string;
    depth_target?: "Basic" | "Standard" | "Deep";
    requested_sources?: string[];
  };
  signed_at: string | null;
  severity_band?: SeverityBand;
}

export interface ApiDiagnosticRecord {
  id: string;
  codename: string;
  real_target_name?: string;
  category: "PE" | "VC" | "CorpMA" | "FamilyOffice";
  persona?: 'operator' | 'investor';
  subject_type?: 'own_company' | 'target_company';
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string;
  headcount_band?: string;
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  requested_sources?: string[];
  status: "queued" | "running" | "complete" | "failed";
  report?: ApiPersistedReport;
  committed_at?: string;
  sandbox_clone_sources?: string[];
  /** W3C / JOURNEY-SPEC.P8 — the reader's own outcome on this case, stamped by POST /api/diagnostics/:id/close. */
  close_decision?: 'invested' | 'passed';
  close_note?: string;
  closed_at?: string;
}

/** Discriminated, intentionally sparse server contract for a scoped read. */
export interface RestrictedReportData {
  kind: 'restricted';
  diagnostic_id: string;
  codename: string;
  status: ApiDiagnosticRecord['status'];
  visible_dimensions: string[];
  scorecard: DimensionScoreRow[];
  partner_review?: PartnerReviewAccess;
}
export interface PartnerReviewAccess {
  second_reader_recommended: boolean;
  partner_reviewer_assigned: boolean;
  viewer_has_second_reader_access: boolean;
}
interface ApiRestrictedDiagnosticRecord {
  kind: 'restricted';
  diagnostic_id: string;
  codename: string;
  status: ApiDiagnosticRecord['status'];
  restricted_view: true;
  visible_dimensions: string[];
  scorecard: ApiDimensionScore[];
  report?: {
    kind: 'restricted';
    diagnostic_id: string;
    scorecard: ApiDimensionScore[];
    partner_review?: PartnerReviewAccess;
  };
}

export interface DiagnosticWaitingRoomPartial {
  id: string;
  label: string;
  value: string;
  detail: string;
}

export interface DiagnosticWaitingRoomSnapshot {
  read_mode?: "company" | "diagnostic";
  diagnostic_id: string;
  codename: string;
  status: "queued" | "running" | "complete" | "failed";
  current_stage: string;
  eta_at: string;
  completed_stage_count: number;
  total_stage_count: number;
  metric_count: number;
  source_count: number;
  sources: string[];
  /** Subset of `sources` that landed at least one metric scoring can band. */
  scoring_sources?: string[];
  earned_depth: {
    band: "Not ready" | "Basic" | "Standard" | "Deep";
    coverage_pct: number;
    source_count: number;
    next: string;
  };
  /** The ceiling this run's sources can reach on the gate's own axis. */
  coverage_projection?: ApiCoverageProjection;
  case_context?: ApiPersistedReport["case_context"];
  scored_dimension_count: number;
  total_dimension_count: number;
  finding_count: number;
  report_ready: boolean;
  partials: DiagnosticWaitingRoomPartial[];
  stages: Array<{ stage: string; phase: string; at: string | null; error: string | null }>;
  latest_signals: Array<{ stage: string; phase: string; at: string; text: string; error?: string }>;
}

export interface DiagnosticWaitingRoomAskResponse {
  diagnostic_id: string;
  codename: string;
  diagnostic_status: ApiDiagnosticRecord["status"];
  answer_status: "answered" | "insufficient_evidence";
  answer: string;
  citations?: Array<{ ref?: string; title?: string; source?: string; observed_at?: string }>;
  trace_id?: string;
  mean_confidence?: number;
  freshness_footer?: string;
  snapshot: DiagnosticWaitingRoomSnapshot;
}

export async function fetchDiagnosticWaitingRoom(
  id: string,
): Promise<DiagnosticWaitingRoomSnapshot> {
  return http<DiagnosticWaitingRoomSnapshot>(
    `/api/diagnostics/${encodeURIComponent(id)}/waiting-room`,
  );
}

export async function askDiagnosticWaitingRoom(
  id: string,
  query: string,
): Promise<DiagnosticWaitingRoomAskResponse> {
  return http<DiagnosticWaitingRoomAskResponse>(
    `/api/diagnostics/${encodeURIComponent(id)}/waiting-room/ask`,
    {
      method: "POST",
      body: JSON.stringify({ query }),
    },
  );
}

export interface CreateDiagnosticRunRequest {
  company_read?: { document_ids: string[]; focus: string; acknowledge_failures: boolean; upload_failures?: string[] };
  codename: string;
  real_target_name?: string;
  category: "PE" | "VC" | "CorpMA" | "FamilyOffice";
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string;
  headcount_band?: "1–25" | "26–100" | "101–500" | "500+";
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  requested_sources?: string[];
  coverage_pct?: number;
  idempotency_key?: string;
  weights?: Record<string, number>;
}

export interface CreateDiagnosticRunResponse {
  id: string;
  status: "queued" | "running" | "complete" | "failed";
  committed_at?: string;
  eta_at?: string;
  codename: string;
  real_target_name?: string;
  category: "PE" | "VC" | "CorpMA" | "FamilyOffice";
  deal_stage?: "just_looking" | "nda" | "term_sheet" | "confirmatory_dd" | "first_look" | "active_diligence" | "confirmatory";
  funding_stage?: "preseed" | "seriesab" | "growth" | "pe";
  sector?: string;
  headcount_band?: string;
  company_url?: string;
  depth_target?: "Basic" | "Standard" | "Deep";
  requested_sources?: string[];
  coverage_pct?: number;
  reused: boolean;
  backend: "production" | "demo" | "limited";
}

export async function createDiagnosticRun(
  input: CreateDiagnosticRunRequest,
): Promise<CreateDiagnosticRunResponse> {
  return http<CreateDiagnosticRunResponse>("/api/diagnostics", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function rerunDiagnostic(
  id: string,
): Promise<CreateDiagnosticRunResponse> {
  return http<CreateDiagnosticRunResponse>(
    `/api/diagnostics/${encodeURIComponent(id)}/rerun`,
    {
      method: "POST",
    },
  );
}

type ApiDiagnosticView = ApiDiagnosticRecord | ApiRestrictedDiagnosticRecord;

function isRestrictedDiagnostic(diagnostic: ApiDiagnosticView): diagnostic is ApiRestrictedDiagnosticRecord {
  return 'kind' in diagnostic && diagnostic.kind === 'restricted';
}

export async function fetchDiagnostics(): Promise<ApiDiagnosticView[]> {
  return http<ApiDiagnosticView[]>("/api/diagnostics");
}

/** Every read of the workspace with its report cut down to what a desk card shows (ZPM-431). */
export async function fetchDiagnosticSummaries(): Promise<ApiDiagnosticView[]> {
  return http<ApiDiagnosticView[]>("/api/diagnostics?view=summary");
}

export function fullDiagnosticRecords(rows: ApiDiagnosticView[]): ApiDiagnosticRecord[] {
  return rows.filter((row): row is ApiDiagnosticRecord => !isRestrictedDiagnostic(row));
}

export async function fetchDiagnosticReports(): Promise<ReportData[]> {
  const diagnostics = await fetchDiagnostics();
  return diagnostics
    .filter((diagnostic): diagnostic is ApiDiagnosticRecord => !isRestrictedDiagnostic(diagnostic) && !!diagnostic.report)
    .sort(compareReadsNewestFirst)
    .map(mapDiagnosticToReportData);
}

export async function fetchLatestDiagnosticReport(): Promise<ReportData | null> {
  const reports = await fetchDiagnosticReports();
  return reports[0] ?? null;
}

export async function fetchLatestDiagnosticReportForRef(
  ref: string,
): Promise<ReportData | null> {
  const reports = await fetchDiagnosticReports();
  const slug = slugifyDiagnosticRef(ref);
  return (
    reports.find(
      (report) =>
        report.meta.diagnostic_id === ref ||
        slugifyDiagnosticRef(report.meta.codename) === slug,
    ) ?? null
  );
}

async function fetchDiagnosticReportView(id: string): Promise<ReportData | RestrictedReportData> {
  let diagnostic: ApiDiagnosticRecord | ApiRestrictedDiagnosticRecord;
  try {
    diagnostic = await http<ApiDiagnosticRecord | ApiRestrictedDiagnosticRecord>(
      `/api/diagnostics/${encodeURIComponent(id)}`,
    );
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error;
    const diagnostics = await fetchDiagnostics();
    const slug = slugifyDiagnosticRef(id);
    const match = diagnostics.find(
      (row) => (isRestrictedDiagnostic(row) ? row.diagnostic_id : row.id) === id || slugifyDiagnosticRef(row.codename) === slug,
    );
    if (!match) throw error;
    diagnostic = match;
  }
  if ('kind' in diagnostic && diagnostic.kind === 'restricted') {
    return {
      kind: 'restricted', diagnostic_id: diagnostic.diagnostic_id, codename: diagnostic.codename,
      status: diagnostic.status, visible_dimensions: diagnostic.visible_dimensions,
      scorecard: (diagnostic.report?.scorecard ?? diagnostic.scorecard).map(toReportScoreRow).filter((row): row is DimensionScoreRow => row !== null),
      partner_review: diagnostic.report?.partner_review,
    };
  }
  if (!diagnostic.report) {
    throw new ApiError(409, {
      error: "report_not_ready",
      status: diagnostic.status,
    });
  }
  return mapDiagnosticToReportData(diagnostic as ApiDiagnosticRecord);
}

/** Full report callers fail closed; the Report screen opts into the discriminated view. */
export function fetchDiagnosticReport(id: string): Promise<ReportData>;
export function fetchDiagnosticReport(id: string, options: { allowRestricted: true }): Promise<ReportData | RestrictedReportData>;
export async function fetchDiagnosticReport(id: string, options?: { allowRestricted: true }): Promise<ReportData | RestrictedReportData> {
  const view = await fetchDiagnosticReportView(id);
  if ('kind' in view && !options?.allowRestricted) throw new ApiError(403, { error: 'restricted_view' });
  return view;
}

/** A company's newest run while it has no signed Read: still running, stopped before signing, or any other state. */
export type NewerReadState = 'running' | 'failed' | 'unavailable';

/** Only a queued or running run is in progress; an unrecognised state (cancelled, expired, legacy) is never shown as one. */
export function newerReadStateFromStatus(status: unknown): NewerReadState {
  if (status === 'queued' || status === 'running') return 'running';
  if (status === 'failed') return 'failed';
  return 'unavailable';
}

/**
 * The saved Read a company tab shows. A selected Read (?read=) is opened exactly. Otherwise the newest signed Read
 * of the company: the codename resolves to the newest run, which has no Read yet while a re-read is running or after
 * one failed, so the tab falls back to the last signed Read and says a newer run exists.
 */
export async function fetchSavedCompanyReport(codename: string, selectedRead?: string | null): Promise<{ report: ReportData | RestrictedReportData | null; newer: NewerReadState | null }> {
  if (selectedRead) return { report: await fetchDiagnosticReport(selectedRead, { allowRestricted: true }), newer: null };
  try {
    return { report: await fetchDiagnosticReport(codename, { allowRestricted: true }), newer: null };
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    const status = typeof error.body === 'object' && error.body !== null ? (error.body as { status?: unknown }).status : undefined;
    const newer = newerReadStateFromStatus(status);
    return { report: await fetchLatestDiagnosticReportForRef(codename), newer };
  }
}

export async function fetchDiagnosticView(id: string): Promise<ReportData | RestrictedReportData> {
  return fetchDiagnosticReport(id, { allowRestricted: true });
}

export interface ScoringExportScorecardRow extends Record<string, string> {
  Dimension: string;
  Metric: string;
  'Current Value': string;
  /** What the value measures; absent on exports persisted before it existed, so renderers fall back to Metric. */
  'Reading Label': string;
  'Normalized Score': string;
  'Observed At': string;
  /** The words a customer surface prints for this reading's date. */
  'Date, as stated': string;
  Period: string;
  'Source System': string;
  'Metric Key': string;
  /** Direct | proxy | alias — how faithfully the ingested key represents the metric. */
  Fidelity: string;
  'Healthy Target': string;
}

export interface ScoringExportEvidenceOnlyRow {
  'Metric Key': string;
  Metric: string;
  'Current Value': string;
  'Source System': string;
  'Observed At': string;
  /** The words this reading's date prints as. Server-decided. */
  'Date, as stated'?: string;
  Period?: string;
  Reason: string;
}

export interface ScoringExportDimensionInputs {
  dimension: string;
  label: string;
  score: number | null;
  confidence: number;
  assessed: boolean;
  unassessable_reason?: string;
  scoring_rows: ScoringExportScorecardRow[];
  evidence_only_rows: ScoringExportEvidenceOnlyRow[];
}

export interface ScoringExport {
  scorecard_rows: Array<Record<string, string>>;
  rationale_rows: Array<Record<string, string | number>>;
  // Per-dimension inputs behind each score.
  dimension_inputs?: ScoringExportDimensionInputs[];
  unmatched_metrics?: Array<{ key: string; source: string }>;
  summary: {
    diagnostic_id: string;
    codename: string;
    composite_score: number;
    severity_band: string;
    stage: string;
    // Metrics ingested after the scoring dedupe, evidence-only readings included.
    total_metrics: number;
    // Distinct metrics that actually contributed to a dimension score.
    scored_metrics?: number;
    scored_dimensions: number;
    pending_dimensions: string[];
    // Fraction of total stage weight actually evidenced (dimensions clearing the confidence floor).
    score_coverage_pct?: number;
    credible_dimension_count?: number;
    missing_dimension_labels?: string[];
  };
}

export async function fetchScoringExport(
  diagnosticId: string,
): Promise<ScoringExport> {
  return http<ScoringExport>(
    `/api/diagnostics/${encodeURIComponent(diagnosticId)}/scoring-export`,
  );
}

// Debug review issues — in-app bug reporting (feature-flagged, removable).
export type DebugIssueStatus = "todo" | "in_dev" | "on_staging" | "verified";

export interface DebugIssue {
  id: string;
  workspace_id: string;
  created_by: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  status: DebugIssueStatus;
  page_url: string;
  page_title: string;
  comment: string;
  marker_x_pct: number;
  marker_y_pct: number;
  marker_w_pct: number;
  marker_h_pct: number;
  screenshot_data_url: string;
  viewport_width: number;
  viewport_height: number;
  user_agent: string;
  assignee?: string;
  resolution_note?: string;
}

export async function createDebugIssue(input: {
  page_url: string;
  page_title: string;
  comment: string;
  marker_x_pct: number;
  marker_y_pct: number;
  marker_w_pct: number;
  marker_h_pct: number;
  screenshot_data_url: string;
  viewport_width: number;
  viewport_height: number;
  user_agent: string;
}): Promise<DebugIssue> {
  return http<DebugIssue>("/api/debug-issues", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function fetchDebugIssues(
  status?: DebugIssueStatus,
): Promise<{ total: number; issues: DebugIssue[] }> {
  const qs = status ? `?status=${status}` : "";
  return http<{ total: number; issues: DebugIssue[] }>(
    `/api/debug-issues${qs}`,
  );
}

export async function updateDebugIssue(
  id: string,
  input: {
    status?: DebugIssueStatus;
    assignee?: string;
    resolution_note?: string;
    comment?: string;
  },
): Promise<DebugIssue> {
  return http<DebugIssue>(`/api/debug-issues/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteDebugIssue(id: string): Promise<void> {
  await http(`/api/debug-issues/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function exportDebugIssues(): Promise<{
  exported_at: string;
  total: number;
  issues: DebugIssue[];
}> {
  return http("/api/debug-issues/export");
}

/** The canonical `/deal/:codename/...` path segment for a report ref. */
export function slugifyDiagnosticRef(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}


const DEAL_STAGE_CONTEXT_LABELS: Record<string, string> = {
  just_looking: "Just-looking",
  nda: "NDA",
  term_sheet: "Term-sheet",
  confirmatory_dd: "Confirmatory DD",
  first_look: "Just-looking",
  active_diligence: "NDA",
  confirmatory: "Confirmatory DD",
};

const FUNDING_STAGE_LABELS: Record<string, string> = {
  preseed: "Pre-seed",
  seriesab: "Series A/B",
  growth: "Growth",
  pe: "PE / buyout",
};

function diagnosticCaseContext(diagnostic: ApiDiagnosticRecord) {
  return diagnostic.report?.case_context ?? {
    deal_stage: diagnostic.deal_stage,
    funding_stage: diagnostic.funding_stage,
    sector: diagnostic.sector,
    headcount_band: diagnostic.headcount_band,
    company_url: diagnostic.company_url,
    depth_target: diagnostic.depth_target,
    requested_sources: diagnostic.requested_sources,
  };
}

function diagnosticSubtitle(diagnostic: ApiDiagnosticRecord): string {
  if (diagnostic.persona === 'operator') return 'your company diagnostic';
  const context = diagnosticCaseContext(diagnostic);
  const parts = [
    context?.funding_stage ? FUNDING_STAGE_LABELS[context.funding_stage] : diagnostic.category,
    context?.sector,
    context?.deal_stage ? DEAL_STAGE_CONTEXT_LABELS[context.deal_stage] : undefined,
    context?.depth_target ? `${context.depth_target} target` : undefined,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : `${diagnostic.category} diagnostic`;
}

function mapDiagnosticToReportData(
  diagnostic: ApiDiagnosticRecord,
): ReportData {
  const report = diagnostic.report!;
  const coverage = report.sync_summary?.coverage_pct ?? 0;
  const scoreCoverage = toScoreCoverage(report);
  const coverageForTier = scoreCoverage?.weight_pct ?? coverage;
  const scorecard = report.scorecard
    .map(toReportScoreRow)
    .filter((row): row is DimensionScoreRow => row !== null);
  const sourceSummaries = report.sync_summary?.sources ?? [];
  const sources =
    sourceSummaries.length > 0
      ? sourceSummaries.map((s) => sourceLabel(s.source))
      : ["Persisted report"];
  return {
    diagnostic_method: report.diagnostic_method,
    evidence_wiki: report.evidence_wiki,
    company_investigation: report.company_investigation,
    meta: {
      diagnostic_id: report.diagnostic_id ?? diagnostic.id,
      codename: diagnostic.codename,
      persona: diagnostic.persona ?? 'investor',
      subject_type: diagnostic.subject_type ?? 'target_company',
      real_name: diagnostic.real_target_name,
      subtitle: diagnosticSubtitle(diagnostic),
      version: "persisted report",
      signed_at: report.signed_at ?? '',
      signed_by: "Zoe",
      sources,
      data_synced_at:
        report.data_synced_at ??
        latestEvidenceObservedAt(report.dimension_evidence) ??
        undefined,
      sandbox_clone_sources: diagnostic.sandbox_clone_sources,
      case_context: diagnosticCaseContext(diagnostic),
    },
    composite_score: report.composite_score,
    readiness: report.readiness,
    severity_band: report.severity_band ?? (typeof report.composite_score === 'number' ? severityBandForScore(report.composite_score) : null),
    diagnostic_class: coverageTierForPct(coverageForTier),
    coverage_pct: coverage,
    source_count: report.sync_summary?.source_count ?? sourceSummaries.length,
    source_count_recorded: typeof report.sync_summary?.source_count === 'number' || sourceSummaries.length > 0,
    verdict_headline: report.verdict_headline?.trim() || (typeof report.composite_score === 'number' ? legacyVerdictHeadline(report.composite_score) : 'Overall score unavailable pending essential evidence.'),
    truth_status: {
      // A verdict headline alone does not establish its provenance.
      verdict: report.truth_status?.verdict ?? 'legacy_fallback',
      recommendations: report.truth_status?.recommendations ?? 'legacy_fallback',
      monitoring_plan: report.truth_status?.monitoring_plan ?? 'legacy_fallback',
      letter:
        report.truth_status?.letter ??
        (report.letter_template_fallback ? 'fallback' : 'legacy_fallback'),
    },
    scoring_provenance: toScoringProvenance(report),
    evidence_read: report.evidence_read,
    company_read: report.company_read,
    score_coverage: scoreCoverage,
    coverage_band:
      report.coverage_band === 'thin' || report.coverage_band === 'standard' || report.coverage_band === 'deep'
        ? report.coverage_band as CoverageBand
        : undefined,
    read_confidence: toReadConfidence(report),
    partner_review: report.partner_review,
    verdict_warnings: report.verdict_warnings ?? report.read_confidence?.warnings ?? [],
    connector_warnings: report.connector_warnings ?? [],
    letter_paragraphs: markdownParagraphs(report.letter_md),
    letter_template_fallback: Boolean(report.letter_template_fallback),
    citation_manifest: report.citation_manifest,
    scorecard,
    findings: report.findings.map(toFindingCard),
    recommendations: report.recommendations,
    monitoring_plan: report.monitoring_plan,
    dimension_evidence: mapDimensionEvidence(report.dimension_evidence),
    verification_gaps: mapVerificationGaps(report.verification_gaps),
  };
}

export const __testMapDiagnosticToReportData = mapDiagnosticToReportData;

function toReadConfidence(report: ApiPersistedReport): ReportData["read_confidence"] {
  const gate = report.read_confidence;
  if (!gate || typeof gate.confidence_pct !== "number") return undefined;
  const decision =
    gate.decision === "auto_sign" || gate.decision === "low_confidence" ? gate.decision : "self_attest";
  const coveragePct = typeof gate.coverage_pct === "number" ? gate.coverage_pct : 0;
  const inputs = toConfidenceInputs(gate);
  return {
    confidence_pct: Math.round(gate.confidence_pct),
    decision,
    thresholds: {
      auto_sign_pct: gate.thresholds?.auto_sign_pct ?? 80,
      self_attest_pct: gate.thresholds?.self_attest_pct ?? 60,
    },
    coverage_tier: coverageTierForPct(coveragePct),
    coverage_pct: coveragePct,
    warnings: gate.warnings ?? [],
    requires_self_attestation: decision === "self_attest",
    ...(inputs.length > 0 ? { inputs } : {}),
  };
}

function toConfidenceInputs(gate: ApiReadConfidence): ReportConfidenceInput[] {
  return (gate.inputs ?? []).flatMap((row) => {
    if (typeof row.dimension !== "string" || row.dimension.length === 0) return [];
    if (typeof row.contribution_pct !== "number" || !Number.isFinite(row.contribution_pct)) return [];
    const num = (value: unknown): number =>
      typeof value === "number" && Number.isFinite(value) ? value : 0;
    return [
      {
        dimension: row.dimension,
        label: typeof row.label === "string" && row.label.length > 0 ? row.label : row.dimension,
        weight: num(row.weight),
        attainment: num(row.attainment),
        contribution_pct: row.contribution_pct,
        attainable_contribution_pct: num(row.attainable_contribution_pct),
        assessed: row.assessed === true,
      },
    ];
  });
}

function toScoreCoverage(report: ApiPersistedReport): ReportData["score_coverage"] {
  const coverage = report.score_coverage;
  if (!coverage) return undefined;
  return {
    weight_pct: Math.max(0, Math.min(100, Math.round(coverage.weight_pct))),
    credible_dimensions: Math.max(0, Math.round(coverage.credible_dimensions)),
    total_dimensions: Math.max(0, Math.round(coverage.total_dimensions)),
    missing_dimensions: coverage.missing_dimensions.filter(isDimension),
  };
}

function toScoringProvenance(report: ApiPersistedReport): ReportData["scoring_provenance"] {
  const snapshot = report.scoring_config_snapshot;
  const version = snapshot?.version ?? report.scoring_config_version;
  if (!version) return undefined;
  const stageWeights = snapshot?.stage_weights ?? [];
  const bands = snapshot?.scoring_bands ?? [];
  const metrics = snapshot?.scoring_metrics ?? [];
  const provenance: NonNullable<ReportData["scoring_provenance"]> = {
    config_version: version,
    stage_weight_count: stageWeights.length,
    scoring_band_count: bands.length,
    scoring_metric_count: metrics.length,
    stage_weight_policy: stageWeights.length > 0 ? "config_snapshot" : "static_fallback",
    metric_weight_policy: metrics.some((metric) => typeof metric.within_dimension_weight === "number" && Number.isFinite(metric.within_dimension_weight))
      ? "within_dimension_weight"
      : "quality_only",
  };
  if (snapshot?.captured_at) provenance.captured_at = snapshot.captured_at;
  return provenance;
}

function latestEvidenceObservedAt(
  evidence: Record<string, ApiDimensionEvidence> | undefined,
): string | undefined {
  if (!evidence) return undefined;
  let latest = 0;
  for (const dim of Object.values(evidence)) {
    for (const row of dim.rows ?? []) {
      const ms = row.observed_at ? Date.parse(row.observed_at) : NaN;
      if (!Number.isNaN(ms) && ms > latest) latest = ms;
    }
  }
  return latest > 0 ? new Date(latest).toISOString() : undefined;
}

function mapDimensionEvidence(
  input: Record<string, ApiDimensionEvidence> | undefined,
): ReportData["dimension_evidence"] {
  if (!input) return undefined;
  const out: Partial<Record<Dimension, ReportDimensionEvidence>> = {};
  for (const [dimension, evidence] of Object.entries(input)) {
    if (!isDimension(dimension)) continue;
    const evidenceOnlyRows = (evidence.evidence_only_rows ?? []).filter(
      (row) => row.source && row.metric_key && typeof row.reason === "string" && row.reason.length > 0,
    ) as ReportDimensionEvidenceOnlyRow[];
    out[dimension] = {
      rows: (evidence.rows ?? []).filter((row) => row.source && row.metric_key),
      ...(evidenceOnlyRows.length > 0 ? { evidence_only_rows: evidenceOnlyRows } : {}),
      score_history: (evidence.score_history ?? []).filter((value) =>
        Number.isFinite(value),
      ),
      narrative: evidence.narrative,
      missing_sources: evidence.missing_sources,
      peer_benchmark: evidence.peer_benchmark,
      predictive_signals: evidence.predictive_signals,
    };
  }
  return out;
}

function mapVerificationGaps(
  input: ApiVerificationGap[] | undefined,
): ReportData["verification_gaps"] {
  if (!input?.length) return undefined;
  return input.map((gap, index) => ({
    id: gap.id || `gap-${index + 1}`,
    ...(gap.dimension && isDimension(gap.dimension) ? { dimension: gap.dimension } : {}),
    claim: gap.claim,
    reason: gap.reason,
    data_gap: gap.data_gap,
    missing_sources: gap.missing_sources ?? [],
    ...(gap.source_sentence ? { source_sentence: gap.source_sentence } : {}),
    ...(gap.no_source_configured ? { no_source_configured: true } : {}),
    freshness_status: gap.freshness_status ?? "unknown",
    citations: gap.citations ?? [],
  }));
}

function toReportScoreRow(input: ApiDimensionScore): DimensionScoreRow | null {
  if (!isDimension(input.dimension)) return null;
  const meta = DIMENSION_META[input.dimension];
  const coverageLabel = input.coverage_label?.trim();
  return {
    dimension: input.dimension,
    label: meta.label,
    sub: coverageLabel
      ? `${meta.description} · ${coverageLabel}`
      : meta.description,
    score: input.confidence >= COMPOSITE_CONFIDENCE_FLOOR ? input.score : 0,
    status: input.confidence >= COMPOSITE_CONFIDENCE_FLOOR ? "scored" : "insufficient",
    delta_30d: "—",
    tone:
      input.confidence < COMPOSITE_CONFIDENCE_FLOOR
        ? "attn"
        : dimensionToneForScore(input.score),
    confidence: input.confidence,
    ...(input.metric_count !== undefined
      ? { metric_count: input.metric_count }
      : {}),
    ...(input.metric_total !== undefined
      ? { metric_total: input.metric_total }
      : {}),
    ...(input.coverage_fidelity
      ? { coverage_fidelity: input.coverage_fidelity }
      : {}),
    ...(coverageLabel ? { coverage_label: coverageLabel } : {}),
  };
}

function toFindingCard(input: ApiFinding): FindingCard {
  const findingEvidence = (input.evidence ?? []).map((item) => ({
    source: item.source,
    label: `${item.reading_name ?? customerReadingName(item.key)} · ${sourceLabel(item.source, item.provenance?.document_title)}`,
    metric_key: item.key,
    value: item.value,
    ...(item.reading_name ? { reading_name: item.reading_name } : {}),
    ...(item.display_value ? { display_value: item.display_value } : {}),
    normalized_score: item.normalized_score,
    observed_at: item.observed_at,
    ...(item.unit !== undefined ? { unit: item.unit } : {}),
    ...(item.period ? {
      period: {
        ...(item.period.label !== undefined ? { label: item.period.label } : {}),
        ...(item.period.start ?? item.period.start_date ? { start: item.period.start ?? item.period.start_date } : {}),
        ...(item.period.end ?? item.period.end_date ? { end: item.period.end ?? item.period.end_date } : {}),
        ...(item.period.kind !== undefined ? { kind: item.period.kind } : {}),
      },
    } : {}),
    ...(item.provenance ? { provenance: item.provenance } : {}),
    ...(item.document_id !== undefined ? { document_id: item.document_id } : {}),
    ...(item.record_id !== undefined ? { record_id: item.record_id } : {}),
    // The server says what the row is evidence of; 'finding' only for a response that predates the field.
    evidence_basis: item.evidence_basis ?? ('finding' as const),
  }));
  return { ...input, evidence: findingEvidence };
}

function isDimension(value: string): value is Dimension {
  return value in DIMENSION_META;
}

function legacyVerdictHeadline(score: number): string {
  const band = severityBandForScore(score);
  if (band === "healthy")
    return "Legacy report fallback: Zoe found a healthy company with a few details worth validating.";
  if (band === "needs_attention")
    return "Legacy report fallback: Zoe found usable signal and several tensions worth knowing.";
  return "Legacy report fallback: Zoe found material risk in the current read.";
}

function markdownParagraphs(markdown: string | null): string[] {
  const paragraphs = (markdown ?? "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && !/^—\s*Zoe$/i.test(p));
  return paragraphs.length > 0
    ? paragraphs
    : ["Zoe has persisted the report, but the letter is not ready yet."];
}

/** How a source is named to a customer. */
function sourceLabel(source: string, document_title?: string): string {
  return customerSourceLabel({ source, ...(document_title ? { document_title } : {}) });
}

export function initGoogleAuth(): void {
  if (typeof window !== "undefined") {
    window.location.href = `${API_BASE}/api/auth/google/init`;
  }
}

export interface SurveyModule {
  id: "enps" | "psychological_safety" | "leadership_alignment";
  label: string;
  scale: { min: number; max: number };
  required: boolean;
}

export type SurveyDispatchStatus =
  | "not_requested"
  | "skipped"
  | "queued"
  | "sent"
  | "partial_failed"
  | "failed";

export interface SurveyCampaign {
  id: string;
  title: string;
  portco_slug: string;
  module_ids?: Array<"enps" | "psychological_safety" | "leadership_alignment">;
  status?: "draft" | "active" | "closed";
  min_responses: number;
  recipient_count: number;
  response_count: number;
  dispatch_status?: SurveyDispatchStatus;
  dispatch_reason?: "no_creds" | "no_recipients" | "send_failed";
  dispatch_sent_count?: number;
  dispatch_failed_count?: number;
  opens_at?: string;
  closes_at?: string;
  closed_at?: string;
}

export interface SurveyDispatch {
  status: SurveyDispatchStatus;
  reason?: "no_creds" | "no_recipients";
  provider?: "resend";
  recipient_count?: number;
  sent_count?: number;
  failed_count?: number;
}

export interface SurveyAnonymityBoundary {
  policy_id: string;
  min_responses: number;
  guarantee: string;
  sources?: Array<{ source: string; boundary: string; status: string; identity_fields_forbidden?: string[] }>;
}

export interface SurveyResults {
  anonymity_boundary?: SurveyAnonymityBoundary;
  campaign_id: string;
  publishable: boolean;
  min_responses: number;
  response_count: number;
  needed: number;
  aggregates?: {
    enps?: number;
    psych_safety_avg?: number;
    leadership_alignment_avg?: number;
  };
}

/** Owner-facing invite link — delivery metadata only, never response data. */
export interface SurveyInviteLink {
  email: string;
  invite_url: string;
  email_status: "queued" | "sent" | "failed" | "skipped" | "not_requested";
  email_error?: string;
}

export async function fetchSurveyModules(): Promise<SurveyModule[]> {
  const res = await http<{ modules: SurveyModule[] }>("/api/surveys/modules");
  return res.modules;
}

/** Workspace survey register — every persisted campaign, newest first. */
export async function fetchSurveyCampaigns(): Promise<{
  campaigns: SurveyCampaign[];
  anonymity_boundary?: SurveyAnonymityBoundary;
}> {
  return http("/api/surveys/campaigns");
}

// Weekly Sunday digest artifact.
export interface WeeklyDigestSegment {
  text: string;
  mark?: "d" | "unk" | "corr";
}

export interface WeeklyDigestResponse {
  week: number;
  company_count: number;
  headline: string;
  paragraphs: WeeklyDigestSegment[][];
  signature_when: string;
  notes: Array<{ label: string; text: string }>;
  numbers: Array<{ name: string; slug?: string; status_label: string; tone: "ok" | "bad" | "open" }>;
  next_week: Array<{ marker?: "plus" | "delta"; label: string; text?: string }>;
  carried_over: Array<{ label: string; status: string; tone: "ok" | "bad" | "open" }>;
}

export async function fetchCurrentDigest(): Promise<WeeklyDigestResponse> {
  return http<WeeklyDigestResponse>("/api/digest/current");
}

export async function createSurveyCampaign(input: {
  title?: string;
  portco_slug?: string;
  modules?: string[];
  recipients_csv?: string;
  send_email?: boolean;
}): Promise<{
  anonymity_boundary?: SurveyAnonymityBoundary;
  campaign: SurveyCampaign;
  recipient_count: number;
  dispatch: SurveyDispatch;
}> {
  return http("/api/surveys/campaigns", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function fetchSurveyResults(
  campaignId: string,
): Promise<SurveyResults> {
  return http<SurveyResults>(
    `/api/surveys/campaigns/${encodeURIComponent(campaignId)}/results`,
  );
}

export async function fetchSurveyInvites(
  campaignId: string,
): Promise<{ campaign_id: string; invites: SurveyInviteLink[] }> {
  return http(
    `/api/surveys/campaigns/${encodeURIComponent(campaignId)}/invites`,
  );
}

export async function submitSurveyResponse(
  token: string,
  input: {
    enps?: number;
    psych_safety?: number;
    leadership_alignment?: number;
  },
): Promise<{ ok: true; submitted_at: string }> {
  return http(`/api/surveys/respond/${capabilityTokenPathSegment(token)}`, capabilityInit(token, {
    method: "POST",
    body: JSON.stringify(input),
  }));
}

export async function fetchSurveyTokenStatus(
  token: string,
): Promise<{ ok: true; modules?: string[] }> {
  return http(`/api/surveys/respond/${capabilityTokenPathSegment(token)}`, capabilityInit(token));
}

// ── QuickBooks AR/AP aging — canonical source buckets, never inferred client-side ──
export interface QuickBooksAgingBuckets {
  current: number;
  thirty: number;
  sixty: number;
  ninety_plus: number;
}

export interface QuickBooksArAgingResponse {
  workspace_id: string;
  portco_slug: string | null;
  as_of: string;
  open_invoice_count: number;
  total_open_usd: number;
  buckets: QuickBooksAgingBuckets;
  past_due_usd: number;
  past_due_pct: number;
}

export interface QuickBooksApAgingResponse {
  workspace_id: string;
  as_of: string;
  scope: 'workspace';
  open_bill_count: number;
  total_open_usd: number;
  buckets: QuickBooksAgingBuckets;
  past_due_usd: number;
}

export function fetchQuickBooksArAging(portcoSlug: string): Promise<QuickBooksArAgingResponse> {
  return http<QuickBooksArAgingResponse>(`/api/sources/quickbooks/ar-aging?portco_slug=${encodeURIComponent(portcoSlug)}`);
}

export function fetchQuickBooksApAging(): Promise<QuickBooksApAgingResponse> {
  return http<QuickBooksApAgingResponse>('/api/sources/quickbooks/ap-aging');
}

export interface DiagnosisReadyNotification {
  assessment_unavailable?: boolean;
  preliminary?: boolean;
  id: string;
  codename: string;
  href: string;
  created_at: string;
}
export function fetchDiagnosisReadyNotifications(): Promise<{ notifications: DiagnosisReadyNotification[] }> {
  return http('/api/notifications/diagnosis-ready');
}
export function dismissDiagnosisReadyNotification(id: string): Promise<{ ok: boolean }> {
  return http(`/api/notifications/diagnosis-ready/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
}

/** Which company this file appears to describe, and whether that agrees with the company under read. */
export interface CompanyFileMatch { verdict: 'match' | 'unclear' | 'mismatch'; reason: string; withheld: boolean; describes?: string; confirmed_at?: string; confirmed_as?: string }
export interface CompanyIntake { codename: string; website: string; focus: string; funding_stage?: 'preseed' | 'seriesab' | 'growth' | 'pe'; archived: boolean; files: Array<{ id: string; title: string; status: 'ready' | 'failed'; detail: string; version: number; chars: number; text_recovery?: 'font_map' | 'ocr'; company_match?: CompanyFileMatch }>; systems: Array<{ source: string; connection_id: string; last_synced_at?: string; selection?: { repositories?: string[]; jira_projects?: string[]; slack_channels?: string[] } }> }
/** The investor's answer to a refusal. */
export const setCompanyFileMatch = (codename: string, documentId: string, belongs: boolean) =>
  http<{ document_id: string; confirmed: boolean; confirmed_as?: string }>(
    `/api/company-intake/${encodeURIComponent(codename)}/documents/${encodeURIComponent(documentId)}/company-match`,
    { method: 'POST', body: JSON.stringify({ belongs }) },
  );
export const fetchCompanyIntake = (codename: string) => http<CompanyIntake>(`/api/company-intake/${encodeURIComponent(codename)}`);
export const saveCompanyIntake = (codename: string, body: { website: string; focus: string; funding_stage?: 'preseed' | 'seriesab' | 'growth' | 'pe'; draft?: boolean }) => http<{ saved: boolean; website: string; funding_stage?: 'preseed' | 'seriesab' | 'growth' | 'pe'; draft?: boolean }>(`/api/company-intake/${encodeURIComponent(codename)}`, { method: 'PUT', body: JSON.stringify(body) });
/** `claim_count` is the number of material claims a parsed PDF put on the claim ledger at upload time. */
export interface CompanyFileReceipt { filename: string; document_id: string; status: string; detail: string; kind?: string; claim_count?: number; text_recovery?: 'font_map' | 'ocr'; /** The same content was already in the data room; nothing new was added. */ deduplicated?: boolean }
export const uploadCompanyFiles = (codename: string, filename: string, content_base64: string) => http<{ files: CompanyFileReceipt[] }>('/api/company-intake/upload', { method: 'POST', body: JSON.stringify({ codename, filename, content_base64 }) });
